/* EditAuditPolicyRenderer.js */

const _ = require("lodash");
const AdvancedFilter = require("@gluenetworks/entity_grid_gl").AdvancedFilter;
const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .InputRadio;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const TextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const CheckboxControl = require("@gluenetworks/steve_ui_core").Control.Boolean.InputCheckbox;
const PopUpWidget = require("@gluenetworks/steve_ui_widgets").PopUp;
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const TestRuleVeil = require("./veils/name/TestRuleComponent");
const JsonEditor = require("@gluenetworks/json-editor");
const {
    ruleUuidsToLabels,
    ruleLabelsToUuids,
    COMMAND_SET_SENTINELS,
} = require("../../../../utils/ruleLabelMapper");
const {buildRuleSchemaEntries} = require("../../../../schemas");
const {buildDynamicSchemas} = require("../../../../utils/buildDynamicSchemas");
const {buildCustomFieldEnumValidator} = require("../../../../utils/customFieldEnumValidator");
const {buildQueryBuilderLeafValidator} = require("../../../../utils/queryBuilderLeafValidator");
const {buildExpressionFieldValidator} = require("../../../../utils/expressionFieldValidator");
const {buildCommandSetOsValidator} = require("../../../../utils/commandSetOsValidator");
const {buildQueryBuilderColumns} = require("../../../../utils/queryBuilderColumns");
const {RULE_UI_ONLY_FIELDS} = require("../../../../utils/ruleUiOnlyFields");

const SOURCE_VALUE_ALL = [
    {label: "Latest", value: "LATEST"},
    {label: "Default", value: "DEFAULT"},
];
const SOURCE_VALUE_AUDIT_ONLY = [{label: "Latest", value: "LATEST"}];
// const deviceAdvFilterProperties = ["accessStatus","configuredBy","configuredInfo","configuredOn","configuredType","connectionInformation.ip","description","detectedHostName","detectedOs","detectedSerialNumber","detectedSku","detectedSoftwareVersion","detectedTypeBase","detectedUptime","detectedVendor","elementCount","fileServerIdentifier","haPairName","licensed","managementState","name","osUpgradedBy","osUpgradedOn","osUpgradeError","osUpgradePlanAction","osUpgradePlanId","osUpgradePlanName","osUpgradeStatus","useIssu","vrf","zoneName"];
const SECTION_VALUE_DEFAULT = [
    {label: "All (Full Snapshot)", value: "FULL"},
    {label: "Block (Start/End)", value: "BLOCK"},
];
const BLOCK_LABELS = {
    start: "Start Line",
    end: "End Line",
};
// Shared by every discard-a-JSON-draft confirmation (Cancel button, back
// breadcrumb, rule swap) so the three flows stay in sync.
const JSON_DISCARD_CONFIRM_INFO = {
    message: "Are you sure you want to cancel? All changes will be lost.",
    title: "Cancel Confirmation Check",
    confirmDetails: {
        text: "Yes",
        type: "text",
    },
};

class EditAuditPolicyViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("edit-audit-policy-view-gl");
        this._rootElem.component = this.model;
        this._logger = this._model._parentView._logger;
        this._renderingForm = false;
        this._currentSource = undefined;
        this.rule = {};
        this._cmdSetMissing = false;
        this._formsComponent = new FormsComponent();
        this._popUpWidget = undefined;
        this._thenFilterExpression = undefined;
        this._elseFilterExpression = undefined;

        this._readOnly = _.get(this._model, "_parentView._isReadOnly", false);

        this._osList = new ComboBoxControl({
            placeholder: "OS",
            darkTheme: false,
        });

        this._commandSetDropdown = new ComboBoxControl({
            placeholder: "Command Set",
            darkTheme: false,
        });
        this._sourceDropdown = new ComboBoxControl({
            placeholder: "Source",
            darkTheme: false,
        });
        this._commandSetResultDropdown = new ComboBoxControl({
            placeholder: "Command Result",
            darkTheme: false,
        });

        this._scopeTextField = new TextControl({
            darkTheme: false,
            placeholder: "Scope",
        });
        this._sectionDropdown = new ComboBoxControl({
            placeholder: "Source",
            darkTheme: false,
        });

        this._startRegexTextField = new TextControl({
            darkTheme: false,
            placeholder: `${BLOCK_LABELS.start} (regular expression)`,
        });
        this._endRegexTextField = new TextControl({
            darkTheme: false,
            placeholder: `${BLOCK_LABELS.end} (regular expression)`,
        });

        this._individualCheckBox = new CheckboxControl({
            darkTheme: false,
            labelShown: true,
            label: "Evaluate each result of the filter individually",
            title: "Evaluate each result of the filter individually",
        });

        this.model._parentView.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "cancelRule",
            handler: (this._onCancelRuleClick = this._onCancelRuleClick.bind(this)),
        });

        // Footer buttons for the rule-level JSON edit mode.
        //   Form mode: JSON (left) — registered after the form loads.
        //   JSON mode: Apply (right, conditional) + Cancel (right).
        // Cancel-then-Apply so the visible footer reads "Cancel  Apply"
        // left-to-right on the right side.
        this.model._parentView.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelJsonRuleEdit",
            handler: (this._onCancelJsonRuleEdit = this._onCancelJsonRuleEdit.bind(this)),
        });
        this.model._parentView.gameEditor.addViewAction({
            type: "normal",
            displayName: "Apply",
            name: "ExitJsonRuleEdit",
            handler: (this._onExitJsonRuleEdit = this._onExitJsonRuleEdit.bind(this)),
        });
        this.model._parentView.gameEditor.hideViewAction("ExitJsonRuleEdit");
        this.model._parentView.gameEditor.hideViewAction("CancelJsonRuleEdit");
        // true while the rule's JSON pane is shown (vs the form).
        this._jsonMode = false;
        // Pre-apply the CSS state so the form doesn't flash before the
        // _formReady block enters JSON mode and re-asserts the class.
        if (_.get(this._model, "_startInJsonMode", false)) {
            this._rootElem.classList.add("json-edit-mode");
        }
        // false until the form + widgets finish rendering; gates the deferred
        // Test Rule / Back / JSON buttons and their handlers.
        this._formReady = false;

        // Observer approach to add wrap off to textarea works but commented as jquery seems to work as well (line 1390)
        // this._mutationObserverCallback = this._mutationObserverCallback.bind(this);
        // this._mutationObserver = new MutationObserver(this._mutationObserverCallback);

        this._showViewActions();
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(
                this._onFormsPendingPatchesChanged.bind(this),
                500,
                false
            ))
        );
        this._osList.on("UserValue", (this._osListChange = this._osListChange.bind(this)));
        this._commandSetDropdown.on(
            "UserValue",
            (this._osCommandSetChange = this._osCommandSetChange.bind(this))
        );
        this._sourceDropdown.on(
            "UserValue",
            (this._osSourceChange = this._osSourceChange.bind(this))
        );
        this._commandSetResultDropdown.on(
            "UserValue",
            (this._osCommandSetResultChange = this._osCommandSetResultChange.bind(this))
        );
        this._scopeTextField.on(
            "UserValue",
            (this._osScopeChange = this._osScopeChange.bind(this))
        );
        this._sectionDropdown.on(
            "UserValue",
            (this._osSectionChange = this._osSectionChange.bind(this))
        );
        this._startRegexTextField.on(
            "UserValue",
            (this._osStartRegexChange = this._osStartRegexChange.bind(this))
        );
        this._endRegexTextField.on(
            "UserValue",
            (this._osEndRegexChange = this._osEndRegexChange.bind(this))
        );
        this._individualCheckBox.on(
            "UserValue",
            (this._osIndividualChange = this._osIndividualChange.bind(this))
        );
    }

    _unregisterListeners() {
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
        this._osList.removeListener("UserValue", this._osListChange);
        this._commandSetDropdown.removeListener("UserValue", this._osCommandSetChange);
        this._sourceDropdown.removeListener("UserValue", this._osSourceChange);
        this._commandSetResultDropdown.removeListener("UserValue", this._osCommandSetResultChange);
        this._scopeTextField.removeListener("UserValue", this._osScopeChange);
        this._sectionDropdown.removeListener("UserValue", this._osSectionChange);
        this._startRegexTextField.removeListener("UserValue", this._osStartRegexChange);
        this._endRegexTextField.removeListener("UserValue", this._osEndRegexChange);
        this._individualCheckBox.removeListener("UserValue", this._osIndividualChange);
    }

    _toggleBlockRegexVisibility() {
        const shouldShowBlockRegex =
            this._commandSetResultDropdown.getValue() !== "all" &&
            this._sectionDropdown.getValue() === "BLOCK";

        if (!this._startRegexFormElement || !this._endRegexFormElement) {
            return;
        }

        if (shouldShowBlockRegex) {
            this._startRegexFormElement.classList.remove("hide");
            this._endRegexFormElement.classList.remove("hide");
        } else {
            this._startRegexFormElement.classList.add("hide");
            this._endRegexFormElement.classList.add("hide");
        }
    }

    _registerFilterTypeListeners() {
        this._filterType.on(
            "UserValue",
            (this._onFilterTypeChange = this._onFilterTypeChange.bind(this))
        );
    }

    _registerAdvancedFilterListeners() {
        this._ifAdvancedFilter.on(
            "AdvancedFilterRuleChanged",
            (this._onIfAdvFilterRuleChange = _.debounce(
                this._onIfAdvFilterRuleChange.bind(this),
                500
            ))
        );
        this._thenAdvancedFilters.forEach((thenAf) => {
            thenAf.on(
                "AdvancedFilterRuleChanged",
                (this._onThenAdvFilterRuleChange = _.debounce(
                    this._onThenAdvFilterRuleChange.bind(this),
                    500
                ))
            );
        });

        this._elseAdvancedFilters.forEach((elseAf) => {
            elseAf.on(
                "AdvancedFilterRuleChanged",
                (this._onElseAdvFilterRuleChange = _.debounce(
                    this._onElseAdvFilterRuleChange.bind(this),
                    500
                ))
            );
        });
    }

    _registerPopUpListener() {
        // this._popUpWidget.on("PopUpVisible", (this._onContent = this._onContent.bind(this)));
    }

    _unregisterPopUpListener() {
        // if (!_.isEmpty(this._popUpWidget)) {
        //     this._popUpWidget.removeListener("PopUpVisible", this._onContent);
        // }
    }

    _onIfAdvFilterRuleChange() {
        if (this._model) {
            this.updateRule();
        }
    }

    _onThenAdvFilterRuleChange() {
        if (this._model) {
            if (
                _.isNull(
                    this._thenAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules")
                )
            ) {
                this.thenContainer.classList.add("error");
            } else {
                this.thenContainer.classList.remove("error");
            }

            this._updateThenAdvancedFilterExpression();

            this.updateRule();
        }
    }

    _onElseAdvFilterRuleChange() {
        if (this._model) {
            this._updateElseAdvancedFilterExpression();
            this.updateRule();
        }
    }

    _onFilterTypeChange(value) {
        this.setFilterSelection(value);
        this.updateRule();
    }

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            const form = this._formsComponent.getValue();

            if (!this._currentSource) {
                this._currentSource = _.get(form, "sourceQuery.source", "LATEST");
            }
            if (_.get(form, "sourceQuery.source", "LATEST") === "LATEST") {
                this._thenAdvancedFilter = this._thenAdvancedFilters[0];
                this._elseAdvancedFilter = this._elseAdvancedFilters[0];

                this._thenAdvancedFilters[0]._renderer
                    .getRootElement()
                    .classList.remove("hidden-filter");
                this._elseAdvancedFilters[0]._renderer
                    .getRootElement()
                    .classList.remove("hidden-filter");
                this._thenAdvancedFilters[1]._renderer
                    .getRootElement()
                    .classList.add("hidden-filter");
                this._elseAdvancedFilters[1]._renderer
                    .getRootElement()
                    .classList.add("hidden-filter");
            } else {
                this._thenAdvancedFilter = this._thenAdvancedFilters[1];
                this._elseAdvancedFilter = this._elseAdvancedFilters[1];

                this._thenAdvancedFilters[0]._renderer
                    .getRootElement()
                    .classList.add("hidden-filter");
                this._elseAdvancedFilters[0]._renderer
                    .getRootElement()
                    .classList.add("hidden-filter");
                this._thenAdvancedFilters[1]._renderer
                    .getRootElement()
                    .classList.remove("hidden-filter");
                this._elseAdvancedFilters[1]._renderer
                    .getRootElement()
                    .classList.remove("hidden-filter");
            }

            if (this._currentSource !== _.get(form, "sourceQuery.source", "LATEST")) {
                this._currentSource = _.get(form, "sourceQuery.source", "LATEST");
                if (_.get(form, "sourceQuery.source", "LATEST") === "LATEST") {
                    if (this._thenFilterExpression) {
                        this._thenAdvancedFilters[0].setQuery(
                            this.findInFilterOpposite(
                                JSON.parse(
                                    JSON.stringify(this._thenFilterExpression).replaceAll(
                                        "defaultConfig",
                                        "latestConfig"
                                    )
                                )
                            )
                        );
                    }
                    if (this._elseFilterExpression) {
                        this._elseAdvancedFilters[0].setQuery(
                            this.findInFilterOpposite(
                                JSON.parse(
                                    JSON.stringify(this._elseFilterExpression).replaceAll(
                                        "defaultConfig",
                                        "latestConfig"
                                    )
                                )
                            )
                        );
                    }
                } else {
                    if (this._thenFilterExpression) {
                        this._thenAdvancedFilters[1].setQuery(
                            this.findInFilterOpposite(
                                JSON.parse(
                                    JSON.stringify(this._thenFilterExpression).replaceAll(
                                        "latestConfig",
                                        "defaultConfig"
                                    )
                                )
                            )
                        );
                    }
                    if (this._elseFilterExpression) {
                        this._elseAdvancedFilters[1].setQuery(
                            this.findInFilterOpposite(
                                JSON.parse(
                                    JSON.stringify(this._elseFilterExpression).replaceAll(
                                        "latestConfig",
                                        "defaultConfig"
                                    )
                                )
                            )
                        );
                    }
                }
            }
            /*
            if(_.get(form, 'sourceQuery.conceptItemId', undefined)) {
                if(document.querySelector('.individual')) {
                    document.querySelector('.individual').classList.remove('disabled');
                }
                this._renderConceptItemCommands();
            } else {
                if(document.querySelector('.individual')) {
                    document.querySelector('.individual').classList.add('disabled');
                    document.querySelector('.individual input-checkbox-control').classList.remove('checked');
                }
                this._removeOldCommandValues();
            }

 */

            this._renderConceptItemCommands();
            this.updateRule(form);
        }
    }

    _mutationObserverCallback(mutationsList) {
        for (const mutation of mutationsList) {
            if (mutation.type === "childList") {
                for (const addedNode of mutation.addedNodes) {
                    if (
                        addedNode.nodeName === "DIV" &&
                        addedNode.classList.contains("filters-container-parent")
                    ) {
                        const textareas = addedNode.querySelectorAll("textarea");
                        textareas.forEach(function (textarea) {
                            textarea.setAttribute("wrap", "off");
                        });
                    }
                }
            }
        }
    }

    _onTestPolicyClick() {
        if (
            _.isNull(this._thenAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules"))
        ) {
            this.thenContainer.classList.add("error");
            return;
        } else {
            this.thenContainer.classList.remove("error");
        }

        const conceptItemMatch = this._sectionDropdown.value.match(/concept-item\.(.+)/);
        const conceptItemId = conceptItemMatch ? conceptItemMatch[1] : undefined;

        const rsiMatch = this._sectionDropdown.value.match(/rsi-id\.(.+)/);
        const rsiId = rsiMatch ? rsiMatch[1] : undefined;

        return this.model._parentView._modalUserFeedback
            .displayCustom(
                new TestRuleVeil(
                    this._model._parentView._logger,
                    this._model._parentView._userSession,
                    Object.assign({}, this._formsComponent.getValue(), {
                        sourceQuery: {
                            os: this._osList.getValue(),
                            conceptItemId: conceptItemId,
                            rsiId: rsiId,
                            query: this._scopeTextField.getValue(),
                            individual: this._individualCheckBox.getValue(),
                        },
                    }),
                    this.model._parentView._modalUserFeedback,
                    this
                ),
                {dontHide: true}
            )
            .catch(() => {
                this.model._parentView._modalUserFeedback.hideDisplayed();
            });
    }

    _isBlockSectionSelected() {
        const sectionHidden = this.commandSetFormContainer
            ? this.commandSetFormContainer
                  .querySelector(".section_elem")
                  ?.classList.contains("hide")
            : true;

        return !sectionHidden && this._sectionDropdown.getValue() === "BLOCK";
    }

    // "Back" button handler (view action "applyRule") — stages the rule
    // into the in-memory policy entity and closes; backend save only
    // happens via the policy-level Save button.
    _applyRuleAndClose() {
        // Blocks a rapid second click; re-enabled wherever this leaves the
        // rule editor open instead of closing it.
        this._disableViewActions();

        const startRegexValue = _.trim(this._startRegexTextField.getValue() || "");
        const endRegexValue = _.trim(this._endRegexTextField.getValue() || "");

        const _showIncompleteRuleMessage = (message, title = "Incomplete Rule") => {
            return this._model._parentView._modalUserFeedback
                .displayNotification(message, {title})
                .catch(() => {
                    this._model._parentView._modalUserFeedback.hideDisplayed();
                })
                .finally(() => {
                    this._enableViewActions();
                });
        };

        if (this._isBlockSectionSelected()) {
            if (_.isEmpty(startRegexValue)) {
                return _showIncompleteRuleMessage(
                    `Rule validation failed. "${BLOCK_LABELS.start}" is a required field.`,
                    "Incomplete Rule"
                ).then(() => false);
            }

            const invalidRegexFields = [];
            const validateRegex = (value, fieldName) => {
                if (_.isEmpty(value)) {
                    return;
                }
                try {
                    new RegExp(value);
                } catch (error) {
                    this._model._logger?.warn({error});
                    invalidRegexFields.push(fieldName);
                }
            };
            validateRegex(startRegexValue, BLOCK_LABELS.start);
            validateRegex(endRegexValue, BLOCK_LABELS.end);
            const plural = invalidRegexFields.length > 1;

            if (!_.isEmpty(invalidRegexFields)) {
                const fieldsLabels = `"${invalidRegexFields.join('" and "')}"`;
                return _showIncompleteRuleMessage(
                    `Rule validation failed. ${fieldsLabels} ${
                        plural ? "must be" : "must be a"
                    } valid regular expression${plural ? "s" : ""}.`,
                    "Invalid Regular Expression"
                ).then(() => false);
            }
        }

        const ruleType = this._filterType.getValue(),
            ifAdvancedFilterQuery =
                this._ifAdvancedFilter._renderer.$builderContainer.queryBuilder("getMongo"),
            thenAdvancedFilterQuery =
                this._thenAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules"),
            elseAdvancedFilterQuery =
                this._elseAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules");

        this._model._userDataStorage.setData(this._osList.getValue(), "selectedOs", true);

        let isInvalid = false;

        switch (ruleType) {
            case "SIMPLE":
                if (!thenAdvancedFilterQuery) {
                    isInvalid = true;
                }
                break;
            case "IF_THEN":
                if (!thenAdvancedFilterQuery || !ifAdvancedFilterQuery) {
                    isInvalid = true;
                }
                break;
            case "IF_THEN_ELSE":
                if (
                    !thenAdvancedFilterQuery ||
                    !ifAdvancedFilterQuery ||
                    !elseAdvancedFilterQuery
                ) {
                    isInvalid = true;
                }
                break;
        }

        const promises = [];
        if (isInvalid) {
            const invalidModal = this._model._parentView._modalUserFeedback.displayConfirmation(
                `Some rules of your IF, THEN or ELSE conditions are incomplete. Would you like to Discard these incomplete rules?`,
                {
                    title: "Incomplete Changes",
                    confirmDetails: {
                        text: "Discard",
                        type: "destructive",
                    },
                    rejectDetails: {
                        text: "Cancel",
                        type: "text",
                    },
                }
            );
            promises.push(invalidModal);
        }

        return Promise.all(promises)
            .then(() => {
                this.updateRule();
                this._stageRuleIntoPolicy();
                return true;
            })
            .catch((err) => {
                // Both branches leave the editor open, so re-enable now.
                this._enableViewActions();
                if (err.userRejected) {
                    this._logger.warn({error: err}, "_applyRuleAndClose() - User Canceled.");
                    return false;
                } else {
                    this._logger.warn({error: err}, "_applyRuleAndClose() - Error");
                    return this._model._parentView._modalUserFeedback
                        .displayError(err)
                        .catch(() => {
                            return Promise.resolve();
                        })
                        .then(() => false);
                }
            });
    }

    // Writes this.rule into the slick row, stages the rules onto the in-memory
    // policy entity (nothing hits the backend until the policy-level Save),
    // and closes the subview. The JSON-draft swap path must NOT call
    // updateRule() first — the hidden form is stale while the JSON pane is up.
    _stageRuleIntoPolicy() {
        const slickDataView = this._model._parentView._renderer._slickDataView;
        Object.assign(slickDataView.getItemById(this._model._currentRow.id), this.rule);
        slickDataView.setItems(slickDataView.getItems());
        this._model._parentView._renderer._refreshSlickGrid();

        this._model._parentView._renderer._setRulesOnSlickChanges(
            this._model._parentView._renderer._slickTable
        );

        // Check if the patch contains the current rule. If yes, the current rule will be set dirty on the grid
        // No need to run this check if audit policy details view is called from history view
        if (_.isFunction(this._model._parentView._auditPolicy.getPendingPatches)) {
            const hasPatchWithCurrentRule = this._model._parentView._auditPolicy.getPendingPatches()
                ? this._model._parentView._auditPolicy
                      .getPendingPatches()
                      .filter(({path}) => path.indexOf("rules/" + this._model._currentRow.id) > -1)
                      .length > 0
                : false;
            if (hasPatchWithCurrentRule || this._model._newRule) {
                _.set(this.rule, "dirty", true);
                Object.assign(slickDataView.getItemById(this._model._currentRow.id), this.rule);
                slickDataView.setItems(slickDataView.getItems());
                this._model._parentView._renderer._refreshSlickGrid();
            }
        }

        this._hideParentViewActions();

        this._onCancelRuleClick();
    }

    _onCancelRuleClick() {
        // Blocks a double-click from re-invoking shutdown() mid-teardown.
        this._disableViewActions();
        this._model._parentView._renderer._childViewRenderer._onBackButtonClick();
    }

    setFilterSelection(value) {
        this.thenContainer.classList.remove("remove-title");

        if (value === "SIMPLE") {
            this.ifContainer.classList.add("hide");
            this.thenContainer.classList.remove("hide");
            this.thenContainer.classList.add("remove-title");
            this.elseContainer.classList.add("hide");
            this.elseConnector.classList.add("hide");
        } else if (value === "IF_THEN") {
            this.ifContainer.classList.remove("hide");
            this.thenContainer.classList.remove("hide");
            this.elseContainer.classList.add("hide");
            this.elseConnector.classList.add("hide");
        } else {
            this.ifContainer.classList.remove("hide");
            this.thenContainer.classList.remove("hide");
            this.elseContainer.classList.remove("hide");
            this.elseConnector.classList.remove("hide");
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._hideViewActions();
        // this._mutationObserver.disconnect();

        if (this._jsonEditor) {
            try {
                this._jsonEditor.destroy();
            } catch (err) {
                /* ignore */
            }
            this._jsonEditor = undefined;
        }

        const promArr = [];
        this._unregisterPopUpListener();
        if (this._ifAdvancedFilter) {
            promArr.push(this._ifAdvancedFilter.shutdown());
        }
        if (this._thenAdvancedFilters.length > 0) {
            this._thenAdvancedFilters.forEach((thenFilter) => promArr.push(thenFilter.shutdown()));
        }
        if (this._elseAdvancedFilters.length > 0) {
            this._elseAdvancedFilters.forEach((elseFilter) => promArr.push(elseFilter.shutdown()));
        }

        return Promise.all([_.invoke(this._popUpWidget, "shutdown")])
            .then(() => Promise.all(promArr))
            .then(
                () => {
                    this._logger = undefined;
                    this._model = undefined;
                    this._renderingForm = undefined;
                    this._rootElem.remove();
                    this._rootElem = undefined;
                    this._currentSource = undefined;
                    this._elseFilterExpression = undefined;
                    this._thenFilterExpression = undefined;
                    this._ifAdvancedFilter = undefined;
                    this._thenAdvancedFilters = undefined;
                    this._thenAdvancedFilter = undefined;
                    this._elseAdvancedFilters = undefined;
                    this._elseAdvancedFilter = undefined;
                    this._cmdSetMissing = undefined;
                },
                (err) => {
                    this._logger.warn(
                        {error: err},
                        "AuditPolicyDetailsViewRenderer.shutdown() error."
                    );
                }
            );
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
        }
        return this.model.whenModelIsReady().then(() => {
            this._renderingForm = true;

            // test for missing custom fields and sanitize
            if (this._model._currentRow.ifExpression) {
                const regex = new RegExp(/"customFieldValues\.([0-9a-z-]*)"/g);
                let filtersJson = JSON.stringify(this._model._currentRow.ifExpression);
                const customFieldMatches = filtersJson.matchAll(regex);
                const customFields = [];
                let customFieldSanitized = false;
                let customFieldTypeMissing = false;

                for (const match of customFieldMatches) {
                    customFields.push(match[1]);
                }
                for (const customFieldId of customFields) {
                    //test for missing custom fields
                    if (!this._model._customFieldsWithInherited[customFieldId]) {
                        customFieldSanitized = true;
                        filtersJson = filtersJson.replace(
                            "customFieldValues." + customFieldId,
                            "-1"
                        );
                    }

                    //test for missing enum type of existing custom enumerator field
                    else if (
                        this._model._customFieldsWithInherited[customFieldId].dataType === "Enum"
                    ) {
                        const typeInfoIds = _.get(
                            this._model,
                            "_customFieldsWithInherited[" + customFieldId + "].typeInfo.ids",
                            []
                        );
                        const exactEnumField = `{"customFieldValues.${customFieldId}":{([^}]*)`;
                        const enumFieldExpression = new RegExp(exactEnumField);
                        //this will look for and find the exact enum field value and objectify it for checking
                        const enumFieldIterator = filtersJson.match(enumFieldExpression);
                        const enumFieldValue = JSON.parse("{" + enumFieldIterator[1] + "}");

                        //finally, check if we have an operator value not on the existing list
                        for (const operator in enumFieldValue) {
                            if (!typeInfoIds.find(enumFieldValue[operator])) {
                                customFieldTypeMissing = true;
                            }
                        }
                    }
                }

                if (customFieldSanitized || customFieldTypeMissing) {
                    let msg = "There’s a problem:\n";
                    if (customFieldSanitized) {
                        msg +=
                            "A rule includes a custom field that doesn’t exist. You'll need to fix the rule.\n";
                    }
                    if (customFieldTypeMissing) {
                        msg +=
                            "A rule includes a custom field value that doesn’t exist. You'll need to fix the rule.";
                    }

                    this._model._currentRow.ifExpression = JSON.parse(filtersJson);

                    //muf of the parent view must be used
                    this._model._parentView._modalUserFeedback.displayError({
                        errorMsg: msg,
                        errorCode: "",
                    });
                }
            }

            // Enabling mutation observer to update textareas once available in DOM
            /*
            const mutationTargetNode = document.getElementsByClassName('lm_item_container focused_item')[0];
            if (mutationTargetNode instanceof Element) {
                const mutationConfig = {
                    childList: true, // Observe direct children
                    subtree: true, // Don't observe child nodes' descendants
                };

                this._mutationObserver.observe(mutationTargetNode, mutationConfig);
            }
            */

            return this._renderEditAuditPolicy();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    _osListChange(val) {
        this._model._getCommandSets(val).then(() => {
            const commandSetLabelMap = [];
            commandSetLabelMap.push({value: "all", label: " All"});
            commandSetLabelMap.push({value: "allConfig", label: " All Configuration"});

            let commandSetObj = commandSetLabelMap.find(
                (i) => i.value === _.get(this.rule, "commandSet", "all")
            );
            let commandSetVal = commandSetObj ? commandSetObj.value : "all";

            let isAuditOnly = false;
            let commandResults = [{value: "all", label: " All"}];
            let isBasicModeEnabled = false;
            const allCommandSets = this._model._parentView._commandSetFeed.getCache();
            const commandSetCount = Object.keys(allCommandSets).length;
            let driftAndAuditExists = false;
            Object.values(allCommandSets).forEach((commandSet) => {
                const cmdSetId = commandSet.getId();
                const cmdSetLabel = commandSet.getName();
                commandSetLabelMap.push({
                    value: cmdSetId,
                    label: cmdSetLabel,
                });
                if (!this._model._parentView.cmdSetValLabelMap[cmdSetId]) {
                    this._model._parentView.cmdSetValLabelMap[cmdSetId] = cmdSetLabel;
                }
                if (commandSet.getDataType() === "AUDIT_ONLY") {
                    isAuditOnly = true;
                }

                if (commandSet.getDataType() === "DRIFT_AND_AUDIT") {
                    driftAndAuditExists = true;
                }

                if (commandSet.getBasicMode()) {
                    isBasicModeEnabled = true;
                }

                if (
                    commandSetVal === "all" ||
                    (commandSetVal === "allConfig" &&
                        commandSet.getDataType() === "DRIFT_AND_AUDIT") ||
                    (commandSetVal === "allOperational" &&
                        commandSet.getDataType() === "AUDIT_ONLY")
                ) {
                    // Show Commands : Start
                    this._getShowCommands(commandSet, commandResults);
                    // Show Commands : End
                    // Concept Items : Start
                    this._getConceptItems(commandSet, commandResults);
                    // Concept Items : End
                    // rsi ids - start
                    this._getRSIIds(commandSet, commandResults);
                    // rsi ids - end
                    // getURL - start
                    this._getGetURLs(commandSet, commandResults);
                    // getURL - end
                }
            });

            if (isAuditOnly) {
                commandSetLabelMap.splice(2, 0, {
                    value: "allOperational",
                    label: " All Operational",
                });

                /* Case where we move from a OS where there are no Operational Command Sets to one which does
                and the selected value is allOperational */
                commandSetObj = commandSetLabelMap.find(
                    (i) => i.value === _.get(this._model._currentRow, "commandSet", "all")
                );
                commandSetVal = commandSetObj ? commandSetObj.value : "all";

                if (commandSetVal === "allOperational") {
                    commandResults = [{value: "all", label: " All"}];
                    Object.values(allCommandSets).forEach((commandSet) => {
                        if (
                            commandSetVal === "all" ||
                            (commandSetVal === "allConfig" &&
                                commandSet.getDataType() === "DRIFT_AND_AUDIT") ||
                            (commandSetVal === "allOperational" &&
                                commandSet.getDataType() === "AUDIT_ONLY")
                        ) {
                            // Show Commands : Start
                            this._getShowCommands(commandSet, commandResults);
                            // Show Commands : End
                            // Concept Items : Start
                            this._getConceptItems(commandSet, commandResults);
                            // Concept Items : End
                            // rsi ids - start
                            this._getRSIIds(commandSet, commandResults);
                            // rsi ids - end
                            // getURL - start
                            this._getGetURLs(commandSet, commandResults);
                            // getURL - end
                        }
                    });
                }
            }

            this._commandSetDropdown.setOptions(commandSetLabelMap);

            if (this._osList.getValue() === "all") {
                commandSetVal = "all";
                this._commandSetDropdown.setValue("all");
                this._commandSetDropdown.setDisabled(true);
            } else {
                commandSetVal = _.get(this._model._currentRow, "commandSet", "all");
                this._commandSetDropdown.setValue(commandSetVal);
                this._commandSetDropdown.setDisabled(false);
            }

            // Source - Start
            if (isAuditOnly) {
                this._sourceDropdown.setOptions(SOURCE_VALUE_AUDIT_ONLY);
                this._sourceDropdown.setValue("LATEST");
                this._sourceDropdown.setDisabled(true);
            } else {
                this._sourceDropdown.setOptions(SOURCE_VALUE_ALL);
                this._sourceDropdown.setValue(_.get(this._model._currentRow, "source", "LATEST"));
                this._sourceDropdown.setDisabled(false);
            }
            // Source - End
            if (this._osList.getValue() === "all") {
                this._commandSetResultDropdown.setOptions([
                    {value: "basicMode", label: " Standard"},
                ]);
                this._commandSetResultDropdown.setValue("basicMode");
                this._commandSetResultDropdown.setDisabled(true);
            } else if (commandSetCount === 0) {
                commandResults.splice(1, 0, {value: "basicMode", label: " Standard"});
                this._commandSetResultDropdown.setOptions(commandResults);
                this._commandSetResultDropdown.setValue("basicMode");
                this._commandSetResultDropdown.setDisabled(false);
            } else if (
                (isBasicModeEnabled || !driftAndAuditExists) &&
                commandSetVal !== "allOperational"
            ) {
                commandResults.splice(1, 0, {value: "basicMode", label: " Standard"});
                this._commandSetResultDropdown.setOptions(commandResults);
                this._commandSetResultDropdown.setValue(isBasicModeEnabled ? "basicMode" : "all");
                this._commandSetResultDropdown.setDisabled(false);
            } else {
                this._commandSetResultDropdown.setOptions(commandResults);
                this._commandSetResultDropdown.setValue(
                    isBasicModeEnabled && commandSetVal !== "allOperational" ? "basicMode" : "all"
                );
                this._commandSetResultDropdown.setDisabled(false);
            }

            this._renderConceptItemCommands();

            if (this._commandSetResultDropdown.getValue() === "basicMode") {
                this.commandSetFormContainer
                    .querySelector(".section_elem")
                    .classList.remove("hide");
            } else {
                this.commandSetFormContainer.querySelector(".section_elem").classList.add("hide");
            }

            const sectionArray = SECTION_VALUE_DEFAULT;
            const selectedLabel = _.get(
                this._osList.getOptions().find((i) => i.value === val),
                "label",
                " All"
            );
            this._getAllRSIIds(sectionArray, selectedLabel);

            this._getAllConceptItems(sectionArray, selectedLabel);
            this._sectionDropdown.setOptions(sectionArray);

            this._enableDisableIndividualCheckbox();
            this.updateRule();
        });
    }

    _osCommandSetChange(val) {
        let isAuditOnly = false;
        const commandResults = [{label: " All", value: "all"}];
        let isBasicModeEnabled = false;
        if (val !== "all" && val !== "allConfig" && val !== "allOperational") {
            const commandSet = this._model._parentView._commandSetFeed.getInstanceById(val);

            // Source dropdown Update - Start
            if (commandSet.getDataType() === "AUDIT_ONLY") {
                isAuditOnly = true;
            }

            if (commandSet.getBasicMode()) {
                isBasicModeEnabled = true;
            }

            if (isAuditOnly) {
                this._sourceDropdown.setOptions(SOURCE_VALUE_AUDIT_ONLY);
                this._sourceDropdown.setValue("LATEST");
                this._sourceDropdown.setDisabled(true);
            } else {
                this._sourceDropdown.setOptions(SOURCE_VALUE_ALL);
                this._sourceDropdown.setValue(_.get(this._model._currentRow, "source", "LATEST"));
                this._sourceDropdown.setDisabled(false);
            }
            // Source dropdown Update - End

            // Command Set Result - Start
            if (isBasicModeEnabled) {
                commandResults.splice(1, 0, {value: "basicMode", label: " Standard"});
            }
            // Show Commands : Start
            this._getShowCommands(commandSet, commandResults);
            // Show Commands : End
            // Concept Items : Start
            this._getConceptItems(commandSet, commandResults);
            // Concept Items : End
            // rsi ids - start
            this._getRSIIds(commandSet, commandResults);
            // rsi ids - end
            // getURL - start
            this._getGetURLs(commandSet, commandResults);
            // getURL - end
            this._commandSetResultDropdown.setOptions(commandResults);
            this._commandSetResultDropdown.setValue(
                isBasicModeEnabled && val !== "allOperational" ? "basicMode" : "all"
            );
            this._renderConceptItemCommands();
            // Command Set Result - End

            if (this._commandSetResultDropdown.getValue() === "basicMode") {
                this.commandSetFormContainer
                    .querySelector(".section_elem")
                    .classList.remove("hide");
            } else {
                this.commandSetFormContainer.querySelector(".section_elem").classList.add("hide");
            }
        } else {
            const commandSetEntities = this._model._parentView._commandSetFeed.getCache();
            const commandSetCount = Object.keys(commandSetEntities).length;
            let driftAndAuditExists = false;
            Object.values(commandSetEntities).forEach((commandSet) => {
                if (commandSet.getDataType() === "AUDIT_ONLY") {
                    isAuditOnly = true;
                }
                if (commandSet.getBasicMode()) {
                    isBasicModeEnabled = true;
                }

                if (commandSet.getDataType() === "DRIFT_AND_AUDIT") {
                    driftAndAuditExists = true;
                }

                // Command Set Result - Start
                if (
                    val === "all" ||
                    (val === "allConfig" && commandSet.getDataType() === "DRIFT_AND_AUDIT") ||
                    (val === "allOperational" && commandSet.getDataType() === "AUDIT_ONLY")
                ) {
                    // Show Commands : Start
                    this._getShowCommands(commandSet, commandResults);
                    // Show Commands : End
                    // Concept Items : Start
                    this._getConceptItems(commandSet, commandResults);
                    // Concept Items : End
                    // rsi ids - start
                    this._getRSIIds(commandSet, commandResults);
                    // rsi ids - end
                    // getURL - start
                    this._getGetURLs(commandSet, commandResults);
                    // getURL - end
                }
                // Command Set Result - End
            });

            // Source - Start
            if (isAuditOnly && (val === "allOperational" || val === "all")) {
                this._sourceDropdown.setOptions(SOURCE_VALUE_AUDIT_ONLY);
                this._sourceDropdown.setValue("LATEST");
                this._sourceDropdown.setDisabled(true);
            } else {
                this._sourceDropdown.setOptions(SOURCE_VALUE_ALL);
                this._sourceDropdown.setValue(_.get(this._model._currentRow, "source", "LATEST"));
                this._sourceDropdown.setDisabled(false);
            }
            // Source - End

            // Command Set Result Setting - Start
            if (commandSetCount === 0) {
                commandResults.splice(1, 0, {value: "basicMode", label: " Standard"});
                this._commandSetResultDropdown.setOptions(commandResults);
                this._commandSetResultDropdown.setValue("basicMode");
            } else if ((isBasicModeEnabled || !driftAndAuditExists) && val !== "allOperational") {
                commandResults.splice(1, 0, {value: "basicMode", label: " Standard"});
                this._commandSetResultDropdown.setOptions(commandResults);
                this._commandSetResultDropdown.setValue(isBasicModeEnabled ? "basicMode" : "all");
            } else {
                this._commandSetResultDropdown.setOptions(commandResults);
                this._commandSetResultDropdown.setValue(
                    isBasicModeEnabled && val !== "allOperational" ? "basicMode" : "all"
                );
            }
            this._renderConceptItemCommands();
            // Command Set Result Setting - End

            if (this._commandSetResultDropdown.getValue() === "basicMode") {
                this.commandSetFormContainer
                    .querySelector(".section_elem")
                    .classList.remove("hide");
            } else {
                this.commandSetFormContainer.querySelector(".section_elem").classList.add("hide");
            }
        }

        this._enableDisableIndividualCheckbox();
        this.updateRule();
    }
    _osSourceChange() {
        this.updateRule();
    }
    _osCommandSetResultChange() {
        const commandSetResultValue = this._commandSetResultDropdown.getValue();
        if (commandSetResultValue === "basicMode") {
            this.commandSetFormContainer.querySelector(".section_elem").classList.remove("hide");
        } else {
            this.commandSetFormContainer.querySelector(".section_elem").classList.add("hide");
        }
        this._enableDisableIndividualCheckbox();
        this._toggleBlockRegexVisibility();
        this.updateRule();
    }
    _osScopeChange() {
        this.updateRule();
    }
    _osSectionChange() {
        this._toggleBlockRegexVisibility();
        this._enableDisableIndividualCheckbox();
        this.updateRule();
    }
    _osStartRegexChange() {
        this.updateRule();
    }
    _osEndRegexChange() {
        this.updateRule();
    }
    _osIndividualChange() {
        this.updateRule();
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _enableDisableIndividualCheckbox() {
        const val = this._commandSetResultDropdown.getValue();
        const val2 = this._sectionDropdown.getValue();

        if (val === "all") {
            const commandResultsExceptRsiAndCi = this._commandSetResultDropdown
                .getOptions()
                .filter(
                    (i) =>
                        i.value !== "all" &&
                        i.value !== "basicMode" &&
                        i.value.indexOf("rsi-id.") === -1 &&
                        i.value.indexOf("concept-item.") === -1
                );
            if (commandResultsExceptRsiAndCi.length === 0) {
                this._individualCheckBox.setDisabled(false);
                return;
            }
        }

        const sectionHidden = this.commandSetFormContainer
            .querySelector(".section_elem")
            .classList.contains("hide");

        if (
            val.indexOf("rsi-id.") > -1 ||
            val.indexOf("concept-item.") > -1 ||
            (!sectionHidden && (val2.indexOf("rsi-id.") > -1 || val2.indexOf("concept-item.") > -1))
        ) {
            this._individualCheckBox.setDisabled(false);
        } else if (this._isBlockSectionSelected()) {
            this._individualCheckBox.setValue(true);
            this._individualCheckBox.setDisabled(true);
        } else {
            this._individualCheckBox.setValue(false);
            this._individualCheckBox.setDisabled(true);
        }
    }

    _showViewActions() {
        const isJsonMode = this._jsonMode;
        // Save / Cancel / Test Rule are only meaningful in form mode — while
        // the JSON pane is up, committing or cancelling would act on a rule
        // state the form hasn't yet absorbed.
        if (isJsonMode) {
            this.model._parentView.gameEditor.hideViewAction("applyRule");
            this.model._parentView.gameEditor.hideViewAction("cancelRule");
            this.model._parentView.gameEditor.hideViewAction("testRule");
        } else {
            this.model._parentView.gameEditor.showViewAction("applyRule");
            this.model._parentView.gameEditor.showViewAction("testRule");
            this.model._parentView.gameEditor.showViewAction("cancelRule");
        }
        this.model._parentView.gameEditor.hideViewAction("addRule");
        // The rule subview always offers the form/JSON toggle; hide the
        // policy-level toggle while we're inside this view so the chrome
        // never shows two competing toggles.
        this.model._parentView.gameEditor.hideViewAction("EnterJsonEdit");
        this.model._parentView.gameEditor.hideViewAction("ExitJsonEdit");
        if (isJsonMode) {
            this.model._parentView.gameEditor.hideViewAction("EnterJsonRuleEdit");
            this.model._parentView.gameEditor.showViewAction("ExitJsonRuleEdit");
            this.model._parentView.gameEditor.showViewAction("CancelJsonRuleEdit");
        } else {
            this.model._parentView.gameEditor.showViewAction("EnterJsonRuleEdit");
            this.model._parentView.gameEditor.hideViewAction("ExitJsonRuleEdit");
            this.model._parentView.gameEditor.hideViewAction("CancelJsonRuleEdit");
        }
        this._hideParentViewActions();
    }

    _hideParentViewActions() {
        this.model._parentView.gameEditor.hideViewAction("DownloadPolicyDetails");
        this.model._parentView.gameEditor.hideViewAction("SavePolicyDetails");
        this.model._parentView.gameEditor.hideViewAction("CancelPolicyDetails");
    }

    _hideViewActions() {
        this.model._parentView.gameEditor.removeViewAction("applyRule");
        this.model._parentView.gameEditor.removeViewAction("testRule");
        this.model._parentView.gameEditor.removeViewAction("cancelRule");
        this.model._parentView.gameEditor.removeViewAction("EnterJsonRuleEdit");
        this.model._parentView.gameEditor.removeViewAction("ExitJsonRuleEdit");
        this.model._parentView.gameEditor.removeViewAction("CancelJsonRuleEdit");
        this.model._parentView.gameEditor.showViewAction("addRule");
        // Restore the policy-level toggle when we leave the rule subview.
        this.model._parentView.gameEditor.showViewAction("EnterJsonEdit");
        if (!this.model._parentView._isChildView) {
            this.model._parentView.gameEditor.showViewAction("DownloadPolicyDetails");
        }
        if (
            this._model._parentView._auditPolicy.isDirty &&
            this._model._parentView._auditPolicy.isDirty()
        ) {
            this.model._parentView.gameEditor.showViewAction("SavePolicyDetails");
            this.model._parentView.gameEditor.showViewAction("CancelPolicyDetails");
        }
    }

    _disableViewActions() {
        this.model._parentView.gameEditor.disableViewAction("applyRule");
        this.model._parentView.gameEditor.disableViewAction("testRule");
        this.model._parentView.gameEditor.disableViewAction("cancelRule");
    }

    /*******************************************/
    /* JSON RULE EDIT MODE                     */
    /*******************************************/

    _ruleLabelMaps() {
        // Build the same map shape that the policy-level renderer uses, but
        // sourcing custom fields from this rule subview's loader (which is
        // already populated by EditAuditPolicyComponent.retrieveCustomFieldsEntity).
        const parentView = this._model._parentView;
        const commandSetUuidToName = {};
        const commandSetOs = {};
        if (parentView._commandSetFeed && parentView._commandSetFeed.cache) {
            Object.keys(parentView._commandSetFeed.cache).forEach((commandSetId) => {
                commandSetUuidToName[commandSetId] =
                    parentView._commandSetFeed.cache[commandSetId].getName();
                commandSetOs[commandSetId] = parentView._commandSetFeed.cache[commandSetId].getOs();
            });
        }
        Object.assign(
            commandSetUuidToName,
            COMMAND_SET_SENTINELS,
            parentView.cmdSetValLabelMap || {}
        );
        return {
            conceptItems: (parentView._gameEditor && parentView._gameEditor.conceptItems) || {},
            rsiIdsMap: (parentView._gameEditor && parentView._gameEditor._rsiIdsMap) || {},
            commandSets: commandSetUuidToName,
            commandSetOs,
            customFields: this._model._customFieldsWithInherited || {},
            tags: parentView._tagIdToNameMap || {},
            qbColumns: buildQueryBuilderColumns(),
        };
    }

    _ensureRuleJsonContainer() {
        if (this._jsonContainer) {
            return this._jsonContainer;
        }
        this._jsonContainer = document.createElement("div");
        // Sizing + visibility are driven by .audit-policy-rule-json-pane and
        // the .json-edit-mode toggle on _rootElem — see _edit_policy.layout.scss.
        this._jsonContainer.classList.add("audit-policy-rule-json-pane", "hidden");
        this._rootElem.appendChild(this._jsonContainer);
        return this._jsonContainer;
    }

    _onEnterJsonRuleEdit() {
        if (this._jsonMode) {
            return;
        }
        this._jsonMode = true;
        this._enterJsonRuleMode();
    }

    _onExitJsonRuleEdit() {
        // Accept path: parse JSON and merge into the rule.
        if (!this._jsonMode) {
            return;
        }
        this._jsonMode = false;
        this._exitJsonRuleMode();
    }

    _onCancelJsonRuleEdit() {
        // Cancel path: discard the JSON draft entirely. _exitJsonRuleMode
        // consults the _jsonCancelling flag and skips the parse/apply
        // step when set. A dirty draft gets a confirmation veil first;
        // "No" stays in the JSON pane with the draft intact.
        if (!this._jsonMode) {
            return;
        }
        if (!this._jsonHasDraft) {
            this._closeJsonRulePane();
            return;
        }
        this.model._parentView.gameEditor.runQuickConfirmationActionWorkflow(
            JSON_DISCARD_CONFIRM_INFO,
            () => {
                this._closeJsonRulePane();
                // runQuickConfirmationActionWorkflow chains .then() on the
                // handler's return value.
                return Promise.resolve();
            }
        );
    }

    // Mode shutdown/teardown only: hide the Ace pane, restore the form pane
    // and the form-mode footer actions, and re-render the form. The
    // _jsonCancelling flag tells _exitJsonRuleMode to skip its parse/apply
    // step, so the pane content is never written back — with a clean pane
    // (no draft) that's a no-op either way; with a dirty draft the caller
    // has already confirmed the discard.
    _closeJsonRulePane() {
        this._jsonCancelling = true;
        this._jsonMode = false;
        this._exitJsonRuleMode();
    }

    _onGoBackFromSubview() {
        if (!this._jsonMode) {
            return this._applyRuleAndClose();
        }
        if (!this._jsonHasDraft) {
            // Clean pane: nothing to discard. _applyRuleAndClose still stages
            // form-side edits and closes — Back's usual form-mode contract.
            this._closeJsonRulePane();
            return this._applyRuleAndClose();
        }
        // The returned promise gates ChildViewRenderer's teardown
        // (goBackCallback().then(shutdown)); never resolves on "No".
        return new Promise((resolve) => {
            this.model._parentView.gameEditor.runQuickConfirmationActionWorkflow(
                JSON_DISCARD_CONFIRM_INFO,
                () => {
                    this._closeJsonRulePane();
                    const applyPromise = this._applyRuleAndClose();
                    // Adopt the apply promise so the outer one settles only once apply-and-close finishes (no teardown mid-apply).
                    resolve(applyPromise);
                    // Hand the workflow's .then() the same promise so its veil sequencing brackets the apply.
                    return applyPromise;
                }
            );
        });
    }

    // Rule-swap (prev/next chevron) counterpart of _onGoBackFromSubview.
    // Unlike that flow, this promise MUST also resolve on "No" — the caller
    // aborts the swap on proceed:false instead of tearing down the child view.
    _prepareRuleSwap() {
        if (!this._jsonMode) {
            return this._applyRuleAndClose().then((applied) => ({
                proceed: applied !== false,
                resumeJson: false,
            }));
        }
        if (!this._jsonHasDraft) {
            this._closeJsonRulePane();
            return this._applyRuleAndClose().then((applied) => ({
                proceed: applied !== false,
                resumeJson: true,
            }));
        }
        const hasErrors = (this._jsonValidationErrors || []).length > 0;
        if (!hasErrors && this._applyJsonDraftToRule()) {
            // Valid draft: apply as Accept does, but skip updateRule() and the
            // form re-render — the subview is torn down right after.
            this._stageRuleIntoPolicy();
            return Promise.resolve({proceed: true, resumeJson: true});
        }
        return new Promise((resolve) => {
            this.model._parentView.gameEditor.runQuickConfirmationActionWorkflow(
                JSON_DISCARD_CONFIRM_INFO,
                () => {
                    this._closeJsonRulePane();
                    return this._applyRuleAndClose().then((applied) =>
                        resolve({proceed: applied !== false, resumeJson: true})
                    );
                },
                () => {
                    resolve({proceed: false, resumeJson: true});
                    return Promise.resolve();
                }
            );
        });
    }

    _refreshJsonRuleAcceptEnabled() {
        const parent = this.model && this.model._parentView;
        if (!parent || !parent.gameEditor) {
            return;
        }
        const hasErrors = (this._jsonValidationErrors || []).length > 0;
        if (this._jsonHasDraft && !hasErrors) {
            parent.gameEditor.enableViewAction("ExitJsonRuleEdit");
        } else {
            parent.gameEditor.disableViewAction("ExitJsonRuleEdit");
        }
    }

    _resetJsonDraftState() {
        this._jsonHasDraft = false;
        this._jsonValidationErrors = [];
        this._jsonInitialContent = undefined;
    }

    // Toggle `.json-edit-mode` on the host — CSS in `_edit_policy.layout.scss`
    // hides form-side siblings and pins the host so the absolutely-positioned
    // JSON pane has something to size against (the rule subview's host has no
    // native sizing).
    _setFormPaneVisible(visible) {
        if (!this._rootElem) {
            return;
        }
        this._rootElem.classList.toggle("json-edit-mode", !visible);
    }

    _enterJsonRuleMode() {
        this._ensureRuleJsonContainer();

        // Flush in-flight form state into `this.rule` before serializing:
        // _onFormsPendingPatchesChanged is debounced and updateRule() pulls
        // the current dropdown/filter values across. The merged projection
        // below picks up edits that haven't reached _currentRow yet.
        if (this._onFormsPendingPatchesChanged && this._onFormsPendingPatchesChanged.flush) {
            try {
                this._onFormsPendingPatchesChanged.flush();
            } catch (flushError) {
                this._logger.warn(
                    {error: flushError},
                    "EditAuditPolicyRenderer._enterJsonRuleMode() failed to flush pending form patches."
                );
            }
        }
        try {
            this.updateRule();
        } catch (updateError) {
            this._logger.warn(
                {error: updateError},
                "EditAuditPolicyRenderer._enterJsonRuleMode() failed to pull current form state into this.rule."
            );
        }

        // Build labelized projection from the live rule. Strip the UI-only
        // fields (severity / source / OS lookup maps, dirty flag, etc.) that
        // _mapRulesForSlick stamps onto rows for in-grid display — the JSON
        // pane should show only the canonical rule shape.
        const labelMaps = this._ruleLabelMaps();
        const mergedRule = _.omit(
            Object.assign(
                {},
                _.cloneDeep(this._model._currentRow || {}),
                _.cloneDeep(this.rule || {})
            ),
            RULE_UI_ONLY_FIELDS
        );
        const {rule: labelizedRule} = ruleUuidsToLabels(mergedRule, labelMaps);
        // Build runtime-tightened schemas (label enums for permissionLevel /
        // os / commandSet / conceptItemId / rsiId sourced from the
        // currently-loaded policy) and a post-validator for enum-typed
        // custom-field option values inside expressions.
        const parentView = this._model._parentView;
        const qbColumns = buildQueryBuilderColumns();
        const dynamicSchemas = buildDynamicSchemas({
            labelMaps,
            osMap: parentView._osMap || {},
            permLevels: parentView._permLevels || [],
            qbColumns,
        });
        const schemaEntries = buildRuleSchemaEntries(labelizedRule, dynamicSchemas);
        const customFieldEnumValidator = buildCustomFieldEnumValidator(labelMaps);
        const queryBuilderLeafValidator = buildQueryBuilderLeafValidator(qbColumns);
        // Authoritative device-column set for this rule's ifExpression — the
        // same columnConfig (base + nodeProps) the form's filter is built from.
        const expressionFieldValidator = buildExpressionFieldValidator(
            Object.keys(this._model.columnConfig || {})
        );
        const commandSetOsValidator = buildCommandSetOsValidator(labelMaps);
        const extraValidators = [
            customFieldEnumValidator,
            queryBuilderLeafValidator,
            expressionFieldValidator,
            commandSetOsValidator,
        ];

        this._resetJsonDraftState();
        this._jsonInitialContent = _.cloneDeep(labelizedRule);

        const onUserChangedContent = ({newValue}) => {
            const reverted = !_.isString(newValue) && _.isEqual(newValue, this._jsonInitialContent);
            this._jsonHasDraft = !reverted;
            this._refreshJsonRuleAcceptEnabled();
        };
        const onValidationChange = ({errors}) => {
            this._jsonValidationErrors = errors || [];
            this._refreshJsonRuleAcceptEnabled();
        };

        if (!this._jsonEditor) {
            this._jsonEditor = JsonEditor.create({
                container: this._jsonContainer,
                schemas: schemaEntries,
                extraValidators,
                initialContent: labelizedRule,
                onUserChangedContent,
                onValidationChange,
            });
        } else {
            // Install schemas/validators silently — setContent below triggers
            // the single validation pass for this entry.
            this._jsonEditor.setSchemas(schemaEntries, {skipValidation: true});
            this._jsonEditor.setExtraValidators(extraValidators, {skipValidation: true});
            this._jsonEditor.setContent(labelizedRule);
        }

        // Hide every form-side sibling under _rootElem; show the JSON pane.
        this._setFormPaneVisible(false);
        this._jsonContainer.classList.remove("hidden");

        this.model._parentView.gameEditor.hideViewAction("EnterJsonRuleEdit");
        this.model._parentView.gameEditor.showViewAction("ExitJsonRuleEdit");
        this.model._parentView.gameEditor.showViewAction("CancelJsonRuleEdit");
        // Hide "Back" (applyRule), "Cancel" (cancelRule), and "Test Rule"
        // while JSON mode is active — these all act on a form state the
        // user can't currently see (the form hasn't absorbed the JSON
        // edits yet) so exposing them is misleading.
        this.model._parentView.gameEditor.hideViewAction("applyRule");
        this.model._parentView.gameEditor.hideViewAction("cancelRule");
        this.model._parentView.gameEditor.hideViewAction("testRule");
        // Accept starts disabled — nothing has been edited yet.
        this._refreshJsonRuleAcceptEnabled();
        this._jsonEditor.resize();
        this._jsonEditor.focus();
    }

    // Parses the JSON pane and merges the resolved rule into _currentRow and
    // this.rule; returns true when a parsed object was applied. Shared by the
    // Accept path (_exitJsonRuleMode) and the swap path (_prepareRuleSwap).
    _applyJsonDraftToRule() {
        if (!this._jsonEditor) {
            return false;
        }
        const {parsed: parsedRule} = this._jsonEditor.runValidation();
        if (!parsedRule || !_.isObject(parsedRule)) {
            return false;
        }
        const {rule: ruleWithUuids, errors: labelErrors} = ruleLabelsToUuids(
            parsedRule,
            this._ruleLabelMaps()
        );
        if (labelErrors && labelErrors.length) {
            // Live validators already disable Accept for unrecognized names, so
            // reaching here means empty label maps. Log rather than block.
            this._logger.warn(
                {errors: labelErrors},
                "EditAuditPolicyRenderer._applyJsonDraftToRule() unresolved label references."
            );
        }
        // Strip UI-only keys so a hand-pasted payload can't overwrite the
        // SlickGrid bookkeeping (`dirty`, lookup maps) the re-render needs.
        const ruleToApply = _.omit(ruleWithUuids, RULE_UI_ONLY_FIELDS);
        // Merge into a CLONE, not the shared grid/entity row _currentRow
        // aliases — so Back stages the edits and Cancel truly discards them.
        this._model._currentRow = Object.assign(
            {},
            _.cloneDeep(this._model._currentRow || {}),
            ruleToApply
        );
        this.rule = Object.assign({}, this.rule || {}, ruleToApply);
        return true;
    }

    _exitJsonRuleMode() {
        // Two callers:
        //   - Accept (ExitJsonRuleEdit button) → parse + apply.
        //   - Cancel (CancelJsonRuleEdit button) → discard the draft.
        // _onCancelJsonRuleEdit sets _jsonCancelling = true before the
        // mode flip; consume + clear the flag here.
        const cancelling = !!this._jsonCancelling;
        this._jsonCancelling = false;

        if (!cancelling) {
            this._applyJsonDraftToRule();
        }
        if (this._jsonContainer) {
            this._jsonContainer.classList.add("hidden");
        }
        // Re-render the form so it reflects any rule changes that came in
        // via the JSON pane. `_renderEditAuditPolicy()` always appends a
        // brand-new .container to _rootElem without removing the previous
        // one, so we strip the old form-side children first — otherwise
        // we end up with two stacked .containers (only the new one carries
        // the active event listeners, so clicks on the still-visible old
        // one appear to do nothing).
        if (this._rootElem) {
            Array.from(this._rootElem.children).forEach((rootChild) => {
                if (rootChild === this._jsonContainer) {
                    return;
                }
                this._rootElem.removeChild(rootChild);
            });
        }
        this._setFormPaneVisible(true);
        this.model._parentView.gameEditor.hideViewAction("ExitJsonRuleEdit");
        this.model._parentView.gameEditor.hideViewAction("CancelJsonRuleEdit");
        this.model._parentView.gameEditor.showViewAction("EnterJsonRuleEdit");
        this.model._parentView.gameEditor.showViewAction("applyRule");
        this.model._parentView.gameEditor.showViewAction("cancelRule");
        this.model._parentView.gameEditor.showViewAction("testRule");
        if (this._formsComponent && this._renderEditAuditPolicy) {
            this._renderingForm = true;
            this._renderEditAuditPolicy().finally(() => {
                this._renderingForm = false;
            });
        }
    }

    _enableViewActions() {
        this.model._parentView.gameEditor.enableViewAction("applyRule");
        this.model._parentView.gameEditor.enableViewAction("testRule");
        this.model._parentView.gameEditor.enableViewAction("cancelRule");
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    async _renderEditAuditPolicy() {
        const container = document.createElement("div");
        container.classList.add("container");

        if (_.get(this._model, "_parentView._isReadOnly", false)) {
            container.classList.add("disabled");
        }

        this._rootElem.appendChild(container);

        Promise.all([this._createPolicyForm(), this._createCommandSetsForm()])
            .then(() => {
                container.appendChild(this._formsComponent.renderer._rootElem);
                container.appendChild(this.commandSetFormContainer);
                this._renderConceptItemCommands();
                this._enableDisableIndividualCheckbox();
                return this._createFilterSection();
            })
            .then((filterSection) => {
                container.appendChild(filterSection);
                this._renderingForm = false;

                if (!this._formReady) {
                    this._formReady = true;
                    const gameEditor = this.model._parentView.gameEditor;
                    gameEditor.addViewAction({
                        type: "normal",
                        displayName: "Test Rule",
                        name: "testRule",
                        position: "left",
                        handler: (this._onTestPolicyClick = this._onTestPolicyClick.bind(this)),
                    });
                    gameEditor.addViewAction({
                        type: "normal",
                        displayName: "Back",
                        name: "applyRule",
                        handler: (this._applyRuleAndClose = this._applyRuleAndClose.bind(this)),
                    });
                    gameEditor.addViewAction({
                        type: "normal",
                        displayName: "JSON",
                        name: "EnterJsonRuleEdit",
                        position: "left",
                        handler: (this._onEnterJsonRuleEdit = this._onEnterJsonRuleEdit.bind(this)),
                    });
                    if (_.get(this._model, "_parentView._isReadOnly", false)) {
                        gameEditor.disableViewAction("testRule");
                    }
                    // Position the freshly-added buttons for the current mode.
                    this._showViewActions();

                    // Earliest safe point to resume JSON mode: it needs the live
                    // form controls and the view actions registered just above.
                    if (this._model._startInJsonMode) {
                        this._model._startInJsonMode = false;
                        this._onEnterJsonRuleEdit();
                    }
                }
                return this._renderConceptItemCommands();
            })
            .then(() => {
                if (this._cmdSetMissing === true) {
                    const userNotificationMessage =
                        "The selected Command Set for this rule is no longer available and has been reset to 'All' to maintain policy functionality. Please review and update the policy settings as needed.";

                    return this._model._parentView._modalUserFeedback
                        .displayNotification(userNotificationMessage, {
                            title: "Deprecated Command Set Detected",
                            confirmDetails: {
                                text: "OK",
                                type: "text",
                            },
                        })
                        .then(() => {
                            this._cmdSetMissing = false;
                        });
                }
                return Promise.resolve();
            });
    }

    updateRule(form) {
        if (this._model) {
            this._renderConceptItemCommands();

            this.rule = {};
            if (!form) {
                form = this._formsComponent.getValue();
            }
            if (form) {
                this.rule.dirty = false;
                this.rule.name = _.get(form, "name", "");
                this.rule.description = _.get(form, "description", "");
                this.rule.enable = _.get(form, "enable", "");
                this.rule.severity = _.get(form, "severity", "INFO");

                this.rule.os = this._osList.getValue() || "all";
                this.rule.commandSet = this._commandSetDropdown.getValue() || "allConfig";

                this.rule.source = this._sourceDropdown.getValue() || "LATEST";

                // Command Set Result - Start
                const commandSetResultValue = this._commandSetResultDropdown.getValue();
                if (commandSetResultValue && commandSetResultValue.indexOf("concept-item.") > -1) {
                    const conceptItemId = commandSetResultValue.replace(/concept-item./, "");
                    this.rule.commandSetResult = {
                        type: "conceptItems",
                        conceptItemId: conceptItemId,
                    };
                    this.rule.conceptItemId = undefined;
                    this.rule.rsiId = undefined;
                } else if (commandSetResultValue && commandSetResultValue.indexOf("rsi-id.") > -1) {
                    const rsiId = commandSetResultValue.replace(/rsi-id./, "");
                    // const rsi = _.get(this.model._parentView.gameEditor, '_rsiIds', []).find(i => i.value === rsiId);
                    this.rule.commandSetResult = {
                        type: "restSyncItems",
                        restSyncItemId: rsiId,
                    };
                    this.rule.conceptItemId = undefined;
                    this.rule.rsiId = undefined;
                } else if (
                    commandSetResultValue &&
                    commandSetResultValue.indexOf("show-command.") > -1
                ) {
                    const command = commandSetResultValue.replace(/show-command./, "");
                    this.rule.commandSetResult = {
                        type: "cliCommands",
                        command: command,
                    };
                    this.rule.conceptItemId = undefined;
                    this.rule.rsiId = undefined;
                } else if (
                    commandSetResultValue &&
                    commandSetResultValue.indexOf("get-url.") > -1
                ) {
                    const getURLVal = commandSetResultValue.replace(/get-url./, "");
                    this.rule.commandSetResult = {
                        type: "getUrls",
                        getUrl: getURLVal,
                    };
                    this.rule.conceptItemId = undefined;
                    this.rule.rsiId = undefined;
                } else if (
                    commandSetResultValue &&
                    commandSetResultValue.indexOf("basicMode") > -1
                ) {
                    this.rule.commandSetResult = {
                        type: "basicMode",
                    };

                    if (this._sectionDropdown.getValue().indexOf("concept-item.") > -1) {
                        this.rule.conceptItemId = this._sectionDropdown
                            .getValue()
                            .replace(/concept-item./, "");
                    } else if (this._sectionDropdown.getValue().indexOf("rsi-id.") > -1) {
                        this.rule.rsiId = this._sectionDropdown.getValue().replace(/rsi-id./, "");
                    } else {
                        if (_.get(this._model._currentRow, "conceptItemId") === null) {
                            this.rule.conceptItemId = null;
                        } else {
                            this.rule.conceptItemId = undefined;
                        }

                        this.rule.rsiId = undefined;
                    }
                } else {
                    if (_.get(this._model._currentRow, "conceptItemId") === null) {
                        this.rule.conceptItemId = null;
                    } else {
                        this.rule.conceptItemId = undefined;
                    }
                    this.rule.rsiId = undefined;
                    this.rule.commandSetResult = {
                        type: "all",
                    };
                }
                // Command Set Result - End

                // Block regex
                if (this._isBlockSectionSelected()) {
                    this.rule.startRegex = this._startRegexTextField.getValue();
                    this.rule.endRegex = this._endRegexTextField.getValue();
                } else {
                    this.rule.startRegex = undefined;
                    this.rule.endRegex = undefined;
                }
                // End block regex

                this.rule.scope = this._scopeTextField.getValue();
                this.rule.scopeSeparateResults = this._individualCheckBox.getValue();

                this.rule.type = this._filterType.getValue();
                const ifAdvancedFilterQuery =
                    this._ifAdvancedFilter._renderer.$builderContainer.queryBuilder("getMongo");
                switch (this.rule.type) {
                    case "SIMPLE":
                        this._updateThenAdvancedFilterExpression();
                        this.rule.ifExpression = undefined;
                        if (this._thenFilterExpression) {
                            this.rule.thenExpression = this._thenFilterExpression;
                        }
                        this.rule.elseExpression = undefined;
                        break;
                    case "IF_THEN":
                        this._updateThenAdvancedFilterExpression();
                        if (ifAdvancedFilterQuery) {
                            this.rule.ifExpression = ifAdvancedFilterQuery;
                        }
                        if (this._thenFilterExpression) {
                            this.rule.thenExpression = this._thenFilterExpression;
                        }
                        this.rule.elseExpression = undefined;
                        break;
                    case "IF_THEN_ELSE":
                        this._updateThenAdvancedFilterExpression();

                        this._updateElseAdvancedFilterExpression();

                        if (ifAdvancedFilterQuery) {
                            this.rule.ifExpression = ifAdvancedFilterQuery;
                        }

                        if (this._thenFilterExpression) {
                            this.rule.thenExpression = this._thenFilterExpression;
                        }
                        if (this._elseFilterExpression) {
                            this.rule.elseExpression = this._elseFilterExpression;
                        }
                }
            }
        }
    }

    findInFilter(input, filter = []) {
        const inputClone = _.cloneDeep(input);
        if (_.get(inputClone, "rules", false)) {
            inputClone.rules.forEach((currentRule, idx) => {
                if (
                    currentRule.hasOwnProperty("value") &&
                    !_.isEmpty(currentRule.value) &&
                    !_.isArray(currentRule.value)
                ) {
                    if (currentRule.value) {
                        currentRule.value = currentRule.value.trimEnd();
                    }
                    inputClone.rules[idx].value = currentRule.value.split("\n");
                } else if (_.isArray(currentRule.value)) {
                    inputClone.rules[idx].value = currentRule.value;
                }
                if (currentRule.hasOwnProperty("rules")) {
                    this.findInFilter(currentRule, filter);
                }
            });
        }
        return inputClone;
    }

    findInFilterOpposite(input, filter = []) {
        const inputClone = _.cloneDeep(input);
        if (_.get(inputClone, "rules", false)) {
            inputClone.rules.forEach((currentRule, idx) => {
                if (currentRule.hasOwnProperty("value") && !_.isEmpty(currentRule.value)) {
                    if (typeof currentRule.value !== "string") {
                        inputClone.rules[idx].value = currentRule.value.join("\n");
                    }
                }
                if (currentRule.hasOwnProperty("rules")) {
                    this.findInFilterOpposite(currentRule, filter);
                }
            });
        }
        return inputClone;
    }

    _createPolicyForm() {
        const jtype = this._getJtype();
        const pristineValue = this.getPristineValues();

        this._renderingForm = true;
        this._formsComponent.setTypeInformation(jtype, true);
        this._formsComponent.setPristineValue(pristineValue, true);
        return this._formsComponent.renderer.render();
    }

    getPristineValues() {
        const pristineValue = _.clone(this.model._currentRow);

        pristineValue.sourceQuery = {};

        pristineValue.sourceQuery.source = _.get(this.model._currentRow, "source", "LATEST");
        pristineValue.sourceQuery.os = _.get(this.model._currentRow, "os", "any").toLowerCase();
        pristineValue.sourceQuery.individual = _.get(
            this.model._currentRow,
            "scopeSeparateResults",
            false
        );

        pristineValue.sourceQuery.query = _.get(this.model._currentRow, "scope", "");

        return pristineValue;
    }

    _getJtype() {
        const jtype = {},
            // passedRow = this.model._currentRow,
            // osItems = this.model._parentView._osMap,
            conceptItems = this.model._parentView.gameEditor.conceptItems;
        const conceptItemsValues = Object.keys(conceptItems),
            defaultConceptItemValues = [],
            defaultConceptItemLabels = [];

        conceptItemsValues.forEach((key) => {
            const item = conceptItems[key];
            defaultConceptItemValues.push(key);
            defaultConceptItemLabels.push(item.label);
        });

        jtype["name"] = "";
        jtype["name@Display.label"] = "Name";
        jtype["name@Display.readOnly"] = this._readOnly;

        jtype["severity@Enum"] = ["INFO", "MINOR", "MAJOR", "CRITICAL"];
        jtype.severity = "INFO";
        jtype["severity@Required"] = true;
        jtype["severity@Display.label"] = "Severity";
        jtype["severity@Display.enum"] = ["Informational", "Minor", "Major", "Critical"];
        jtype["severity@Display.readOnly"] = this._readOnly;

        jtype["description"] = "";
        jtype["description@Display.label"] = "Description";
        jtype["description@Display.readOnly"] = this._readOnly;

        jtype["enable"] = true;
        jtype["enable@Display.label"] = "Enabled";

        return jtype;
    }

    async _createFilterSection() {
        this.ifContainer = document.createElement("div");
        const condRow = document.createElement("div");
        this.ifContainer.classList.add("if-row");
        condRow.classList.add("cond-row");

        const thenConnector = document.createElement("div");
        thenConnector.classList.add("connector-then");

        this.elseConnector = document.createElement("div");
        const elseLine = document.createElement("div"),
            elseCurve = document.createElement("div"),
            elseLowerLine = document.createElement("div"),
            elseLowerCurve = document.createElement("div"),
            elseLowerHorLine = document.createElement("div");

        this.elseConnector.classList.add("connector-else");
        elseLine.classList.add("connector-else-1");
        elseCurve.classList.add("connector-else-2");
        elseLowerLine.classList.add("connector-else-3");
        elseLowerCurve.classList.add("connector-else-4");
        elseLowerHorLine.classList.add("connector-else-5");
        this.elseConnector.appendChild(elseLine);
        this.elseConnector.appendChild(elseCurve);
        this.elseConnector.appendChild(elseLowerLine);
        this.elseConnector.appendChild(elseLowerCurve);
        this.elseConnector.appendChild(elseLowerHorLine);

        this._filterType = new InputRadioControl({
            labelShown: true,
            horizontal: true,
            options: [
                {
                    label: "Simple",
                    value: "SIMPLE",
                },
                {
                    label: "IF...THEN",
                    value: "IF_THEN",
                },
                {
                    label: "IF...THEN...ELSE",
                    value: "IF_THEN_ELSE",
                },
            ],
            value: this._model._currentRow.type || "SIMPLE",
            label: "Type",
        });

        this._registerFilterTypeListeners();

        const filtersContainerParent = document.createElement("div");
        filtersContainerParent.classList.add("filters-container-parent");
        filtersContainerParent.appendChild(this._filterType._renderer._rootElem);

        const filtersContainer = document.createElement("div");
        filtersContainer.classList.add("filters-container");
        filtersContainerParent.appendChild(filtersContainer);

        this.ifContainer.appendChild(this._renderIfAdvancedFilter());
        this.ifContainer.appendChild(thenConnector);
        filtersContainer.appendChild(this.ifContainer);

        condRow.appendChild(this.elseConnector);
        condRow.appendChild(await this._renderThenAdvancedFilter());
        condRow.appendChild(await this._renderElseAdvancedFilter());
        filtersContainer.appendChild(condRow);
        this.setFilterSelection(this._filterType.getValue());

        this._registerAdvancedFilterListeners();

        //we need wraps off on textarea elements
        // eslint-disable-next-line
        $(filtersContainerParent).find("textarea").attr("wrap", "off");

        return filtersContainerParent;
    }

    _createCommandSetsForm() {
        return Promise.resolve()
            .then(() => {
                this.commandSetFormContainer = document.createElement("div");
                this.commandSetFormContainer.classList.add("command-set-form-container");

                // OS Dropdown - Start
                const osLabelValue = Object.entries(this._model._parentView._osMap).map(
                    ([key, value]) => {
                        return {
                            value: key,
                            label: value,
                        };
                    }
                );
                osLabelValue.unshift({
                    value: "all",
                    label: " All",
                });
                this._osList.setOptions(osLabelValue);
                this._osList.setValue(
                    _.get(this._model._currentRow, "os") === "any"
                        ? "all"
                        : _.get(this._model._currentRow, "os", "all")
                );

                this.commandSetFormContainer.appendChild(
                    this._createElement(this._osList._renderer._rootElem, "os_dropdown", "OS")
                );
                // OS Dropdown - End

                return this._model._getCommandSets(this._osList.getValue() || "allConfig");
            })
            .then(() => {
                // Command Set - Start
                const commandSetLabelMap = [];
                commandSetLabelMap.push({value: "all", label: " All"});
                commandSetLabelMap.push({value: "allConfig", label: " All Configuration"});

                let isAuditOnly = false;
                let commandResults = [{value: "all", label: " All"}];
                let isBasicModeEnabled = false;
                const allCommandSets = this._model._parentView._commandSetFeed.getCache();
                const commandSetCount = Object.keys(allCommandSets).length;
                let driftAndAuditExists = false;

                let cmdSetVal = "all";
                if (this._osList.getValue() === "all") {
                    cmdSetVal = "all";
                } else {
                    cmdSetVal =
                        _.get(this._model._currentRow, "commandSet", null) === null
                            ? "allConfig"
                            : _.get(this._model._currentRow, "commandSet", "all");
                }

                // Checking if the command set still exists and is enabled. If not, fallback to "All"
                const cmdSet = this._model._parentView._commandSetFeed.getInstanceById(cmdSetVal);

                if (
                    cmdSet === null &&
                    cmdSetVal !== "all" &&
                    cmdSetVal !== "allConfig" &&
                    cmdSetVal !== "allOperational"
                ) {
                    cmdSetVal = "all";
                    this._cmdSetMissing = true;
                }

                Object.values(allCommandSets).forEach((commandSet) => {
                    const cmdSetId = commandSet.getId();
                    const cmdSetLabel = commandSet.getName();
                    commandSetLabelMap.push({
                        value: cmdSetId,
                        label: cmdSetLabel,
                    });
                    if (!this._model._parentView.cmdSetValLabelMap[cmdSetId]) {
                        this._model._parentView.cmdSetValLabelMap[cmdSetId] = cmdSetLabel;
                    }

                    if (commandSet.getDataType() === "AUDIT_ONLY") {
                        isAuditOnly = true;
                    }
                    if (commandSet.getDataType() === "DRIFT_AND_AUDIT") {
                        driftAndAuditExists = true;
                    }

                    if (commandSet.getBasicMode()) {
                        isBasicModeEnabled = true;
                    }

                    if (
                        cmdSetVal === "all" ||
                        (cmdSetVal === "allConfig" &&
                            commandSet.getDataType() === "DRIFT_AND_AUDIT") ||
                        (cmdSetVal === "allOperational" &&
                            commandSet.getDataType() === "AUDIT_ONLY")
                    ) {
                        // Show Commands : Start
                        this._getShowCommands(commandSet, commandResults);
                        // Show Commands : End
                        // Concept Items : Start
                        this._getConceptItems(commandSet, commandResults);
                        // Concept Items : End
                        // rsi ids - start
                        this._getRSIIds(commandSet, commandResults);
                        // rsi ids - end
                        // getURL - start
                        this._getGetURLs(commandSet, commandResults);
                        // getURL - end
                    }
                });

                if (isAuditOnly) {
                    commandSetLabelMap.splice(2, 0, {
                        value: "allOperational",
                        label: " All Operational",
                    });
                }

                this._commandSetDropdown.setOptions(commandSetLabelMap);
                if (this._osList.getValue() === "all") {
                    this._commandSetDropdown.setValue("all");
                    this._commandSetDropdown.setDisabled(true);
                } else {
                    this._commandSetDropdown.setValue(cmdSetVal);
                    this._commandSetDropdown.setDisabled(false);
                }

                if (
                    cmdSetVal !== "all" &&
                    cmdSetVal !== "allConfig" &&
                    cmdSetVal !== "allOperational"
                ) {
                    const cmdSet =
                        this._model._parentView._commandSetFeed.getInstanceById(cmdSetVal);
                    commandResults = [{value: "all", label: " All"}];
                    if (cmdSet.getDataType() === "AUDIT_ONLY") {
                        isAuditOnly = true;
                    } else {
                        isAuditOnly = false;
                    }
                    if (cmdSet.getDataType() === "DRIFT_AND_AUDIT") {
                        driftAndAuditExists = true;
                    } else {
                        driftAndAuditExists = false;
                    }

                    if (cmdSet.getBasicMode()) {
                        isBasicModeEnabled = true;
                    } else {
                        isBasicModeEnabled = false;
                    }
                    // Show Commands : Start
                    this._getShowCommands(cmdSet, commandResults);
                    // Show Commands : End
                    // Concept Items : Start
                    this._getConceptItems(cmdSet, commandResults);
                    // Concept Items : End
                    // rsi ids - start
                    this._getRSIIds(cmdSet, commandResults);
                    // rsi ids - end
                    // getURL - start
                    this._getGetURLs(cmdSet, commandResults);
                    // getURL - end
                }

                this.commandSetFormContainer.appendChild(
                    this._createElement(
                        this._commandSetDropdown._renderer._rootElem,
                        "command_set",
                        "Command Set"
                    )
                );
                // Command Set - End
                // Source - Start
                if (isAuditOnly && (cmdSetVal === "allOperational" || cmdSetVal === "all")) {
                    this._sourceDropdown.setOptions(SOURCE_VALUE_AUDIT_ONLY);
                    this._sourceDropdown.setValue("LATEST");
                    this._sourceDropdown.setDisabled(true);
                } else {
                    this._sourceDropdown.setOptions(SOURCE_VALUE_ALL);
                    this._sourceDropdown.setValue(
                        _.get(this._model._currentRow, "source", "LATEST")
                    );
                    this._sourceDropdown.setDisabled(false);
                }
                this.commandSetFormContainer.appendChild(
                    this._createElement(
                        this._sourceDropdown._renderer._rootElem,
                        "source_elem",
                        "Source"
                    )
                );
                // Source - End

                // Command Set Result - Start

                const shouldAddBasicMode =
                    commandSetCount === 0 ||
                    ((isBasicModeEnabled || (cmdSetVal === "all" && !driftAndAuditExists)) &&
                        cmdSetVal !== "allOperational");

                if (shouldAddBasicMode) {
                    commandResults.splice(1, 0, {value: "basicMode", label: " Standard"});
                }

                if (this._osList.getValue() === "all") {
                    this._commandSetResultDropdown.setOptions([
                        {value: "basicMode", label: " Standard"},
                    ]);
                } else {
                    this._commandSetResultDropdown.setOptions(commandResults);
                }

                let prefix = "";
                let val = "";
                if (
                    _.get(this._model._currentRow, "commandSetResult.type", "all") ===
                    "conceptItems"
                ) {
                    prefix = "concept-item.";
                    val = _.get(this._model._currentRow, "commandSetResult.conceptItemId");
                } else if (
                    _.get(this._model._currentRow, "commandSetResult.type", "all") ===
                    "restSyncItems"
                ) {
                    prefix = "rsi-id.";
                    val = _.get(this._model._currentRow, "commandSetResult.restSyncItemId");
                } else if (
                    _.get(this._model._currentRow, "commandSetResult.type", "all") === "cliCommands"
                ) {
                    prefix = "show-command.";
                    val = _.get(this._model._currentRow, "commandSetResult.command");
                } else if (
                    _.get(this._model._currentRow, "commandSetResult.type", "all") === "getUrls"
                ) {
                    prefix = "get-url.";
                    val = _.get(this._model._currentRow, "commandSetResult.getUrl");
                } else if (
                    _.get(this._model._currentRow, "commandSetResult.type", "all") === "basicMode"
                ) {
                    prefix = "";
                    val = "basicMode";
                } else if (
                    _.get(this._model._currentRow, "commandSetResult.type", "all") === "all"
                ) {
                    prefix = "";
                    val = "all";
                }
                if (this._osList.getValue() === "all") {
                    this._commandSetResultDropdown.setValue("basicMode");
                    this._commandSetResultDropdown.setDisabled(true);
                } else {
                    this._commandSetResultDropdown.setValue(`${prefix}${val}` || "basicMode");
                    this._commandSetResultDropdown.setDisabled(false);
                }
                this.commandSetFormContainer.appendChild(
                    this._createElement(
                        this._commandSetResultDropdown._renderer._rootElem,
                        "command_set_result",
                        "Command Result"
                    )
                );

                // Command Set Result - End
                // Scope - Start
                this._scopeTextField.setValue(_.get(this._model._currentRow, "scope", ""));
                this.commandSetFormContainer.appendChild(
                    this._createElement(
                        this._scopeTextField._renderer._rootElem,
                        "scope_elem",
                        "Scope"
                    )
                );
                // Scope - End

                // Section - Start
                const sectionArray = SECTION_VALUE_DEFAULT;
                const selectedLabel = _.get(
                    this._osList.getOptions().find((i) => i.value === this._osList.getValue()),
                    "label",
                    " All"
                );
                this._getAllRSIIds(sectionArray, selectedLabel);

                this._getAllConceptItems(sectionArray, selectedLabel);
                this._sectionDropdown.setOptions(sectionArray);

                this.commandSetFormContainer.appendChild(
                    this._createElement(
                        this._sectionDropdown._renderer._rootElem,
                        "section_elem",
                        "Section"
                    )
                );
                if (this._commandSetResultDropdown.getValue() === "basicMode") {
                    this.commandSetFormContainer
                        .querySelector(".section_elem")
                        .classList.remove("hide");

                    let sectionVal = SECTION_VALUE_DEFAULT[0].value;

                    if (_.get(this._model._currentRow, "conceptItemId", false)) {
                        sectionVal = `concept-item.${_.get(
                            this._model._currentRow,
                            "conceptItemId"
                        )}`;
                    } else if (_.get(this._model._currentRow, "rsiId", false)) {
                        sectionVal = `rsi-id.${_.get(this._model._currentRow, "rsiId")}`;
                    } else if (
                        !_.isEmpty(_.get(this._model._currentRow, "startRegex", "")) ||
                        !_.isEmpty(_.get(this._model._currentRow, "endRegex", ""))
                    ) {
                        sectionVal = "BLOCK";
                        this._individualCheckBox.setValue(true);
                    }

                    this._sectionDropdown.setValue(sectionVal);
                } else {
                    this.commandSetFormContainer
                        .querySelector(".section_elem")
                        .classList.add("hide");
                }
                // Section - End

                this._individualCheckBox.setValue(
                    _.get(this._model._currentRow, "scopeSeparateResults", "")
                );
                this.commandSetFormContainer.appendChild(
                    this._createElement(
                        this._individualCheckBox._renderer._rootElem,
                        "individual_elem"
                    )
                );

                this._startRegexTextField.setValue(
                    _.get(this._model._currentRow, "startRegex", "")
                );
                this._startRegexFormElement = this._createElement(
                    this._startRegexTextField._renderer._rootElem,
                    "start_regex_elem",
                    BLOCK_LABELS.start
                );
                this.commandSetFormContainer.appendChild(this._startRegexFormElement);

                this._endRegexTextField.setValue(_.get(this._model._currentRow, "endRegex", ""));
                this._endRegexFormElement = this._createElement(
                    this._endRegexTextField._renderer._rootElem,
                    "end_regex_elem",
                    BLOCK_LABELS.end
                );
                this.commandSetFormContainer.appendChild(this._endRegexFormElement);

                this._toggleBlockRegexVisibility();
            });
    }

    _createElement(elem, className = undefined, label = undefined) {
        const containerElem = document.createElement("div");
        containerElem.classList.add("element-container");
        if (className) {
            containerElem.classList.add(className);
        }
        if (label) {
            const labelElem = document.createElement("label");
            labelElem.innerHTML = label;
            containerElem.appendChild(labelElem);
        }
        containerElem.appendChild(elem);
        return containerElem;
    }

    _renderIfAdvancedFilter() {
        const customFieldsList = [];

        for (const field in this._model._customFieldsWithInherited) {
            if (
                this._model._customFieldsWithInherited.hasOwnProperty(field) &&
                !this._model._customFieldsWithInherited[field].systemOwned &&
                ["Boolean", "String", "Enum", "Dropdown", "Number"].indexOf(
                    this._model._customFieldsWithInherited[field].dataType
                ) >= 0
            ) {
                customFieldsList.push(
                    Object.assign({}, this._model._customFieldsWithInherited[field], {
                        id: field,
                        name: `customFieldValues.${field}`,
                        label: this._model._customFieldsWithInherited[field].name,
                    })
                );
            }
        }

        this._ifAdvancedFilter = new AdvancedFilter({
            filterData: {},
            columnsConfig: this._getFilterOptions(this._model.columnConfig, customFieldsList),
            allowGroups: true,
        });

        // this._ifAdvancedFilter = new AdvancedFilter({
        //     filterData: {},
        //     columnsData: this._model._deviceEntityDef.$filterProperties
        // });

        const _ifContainer = document.createElement("div");
        const ifLabel = document.createElement("label");
        _ifContainer.classList.add("if-container");
        ifLabel.innerText = "IF";
        _ifContainer.appendChild(ifLabel);
        _ifContainer.appendChild(this._ifAdvancedFilter.renderer._rootElem);

        if (this._model._currentRow.ifExpression) {
            this._ifAdvancedFilter.setQuery(this._model._currentRow.ifExpression);
        }

        return _ifContainer;
    }

    _getFilterOptions(columnsData, customFields) {
        columnsData = Object.keys(columnsData).map((i) =>
            Object.assign({}, columnsData[i], {id: i, field: i, name: i})
        );
        columnsData = this._getFilterFieldsConfig(Object.values(columnsData));
        const filterOptions = [].concat(Object.values(columnsData), customFields);
        const allowedCustomFieldTypes = [
            "boolean",
            "string",
            "enum",
            "number",
            "longstring",
            "array",
        ];
        return filterOptions
            .filter((option) => {
                if (
                    option.dataType &&
                    !allowedCustomFieldTypes.includes(option.dataType.toLowerCase())
                ) {
                    return false;
                }
                return true;
            })
            .map((optData) => {
                const options = {
                    dataType: optData.dataType ? optData.dataType.toLowerCase() : undefined,
                };
                return Object.assign({}, optData, options);
            });
    }

    _getFilterFieldsConfig(columnsConfig) {
        const getDataType = (dataType) => {
            const allowedTypes = [
                "string",
                "integer",
                "double",
                "date",
                "time",
                "datetime",
                "boolean",
            ];
            if (allowedTypes.includes(dataType)) {
                return dataType;
            }
            switch (dataType) {
                case "number":
                    return "integer";
                default:
                    return "string";
            }
        };

        const generateFilterItem = ({name, label, dataType, options, validation, typeInfo}) => {
            const normalisedDataType = getDataType(dataType);

            const filterItem = {
                id: name,
                field: name,
                name: name,
                dataType: dataType,
                label: label,
                type: normalisedDataType,
                size: 35,
            };

            if (dataType === "enum" && typeInfo && !typeInfo.multiSelect) {
                const {ids = [], labels = []} = typeInfo;
                options = labels.map((label, idx) => ({[ids[idx]]: label}));
            }

            if (!_.isEmpty(validation)) {
                Object.assign(filterItem, {validation: validation});
            }

            if (!_.isEmpty(options)) {
                Object.assign(filterItem, {
                    typeInfo: {
                        ids: Object.keys(options).map((i) => options[i].name),
                        labels: Object.keys(options).map((i) => options[i].title),
                        multiSelect: false,
                    },
                    input: "select",
                    operators: ["equal_select", "not_equal_select", "is_empty", "is_not_empty"],
                });
            }
            if (dataType === "longstring") {
                Object.assign(filterItem, {
                    values: options,
                    input: "text",
                    operators: [
                        "longstring_begins_with",
                        "longstring_not_begins_with",
                        "contains",
                        "not_contains",
                        "longstring_ends_with",
                        "longstring_not_ends_with",
                        "is_empty",
                        "is_not_empty",
                    ],
                });
            }

            if (dataType === "boolean") {
                Object.assign(filterItem, {
                    operators: ["equal", "is_empty", "is_not_empty"],
                    input: "radio",
                    default_value: true,
                    values: {
                        1: "Yes",
                        0: "No",
                    },
                    valueSetter: function (rule, value) {
                        value = Number(value) || 0;
                        const name = rule.id + "_value_0";
                        rule.$el
                            .find("[name=" + name + '][value="' + value + '"]')
                            .prop("checked", true)
                            .trigger("change");
                    },
                });
            }

            if (dataType === "date") {
                Object.assign(
                    filterItem,
                    {
                        operators: [
                            "equal",
                            "less",
                            "less_or_equal",
                            "greater",
                            "greater_or_equal",
                            "is_empty",
                            "is_not_empty",
                        ],
                    },
                    this.generateCalendarInput(name)
                );
            }

            return filterItem;
        };
        return columnsConfig.map(generateFilterItem);
    }

    _renderThenAdvancedFilter() {
        this._thenAdvancedFilters = [];
        let column = {},
            testFilter = {};
        column = {
            field: "latestConfig",
            id: "latestConfig",
            input: "textarea",
            label: "Latest Snapshot",
            type: "string",
            values: [],
        };
        testFilter = {
            condition: "AND",
            rules: [
                {
                    id: "latestConfig",
                    operator: "should_contain",
                    value: "a",
                },
            ],
        };

        let type = {
            name: "special",
            filterQuery: testFilter,
            column,
        };
        // const columnData =
        this._thenAdvancedFilters[0] = new AdvancedFilter({
            filterData: {},
            columnsData: [column],
            type: type,
        });

        column = {
            field: "defaultConfig",
            id: "defaultConfig",
            input: "textarea",
            label: "Default Snapshot",
            type: "string",
            values: [],
        };
        testFilter = {
            condition: "AND",
            rules: [
                {
                    id: "defaultConfig",
                    operator: "should_contain",
                    value: "a",
                },
            ],
        };

        type = {
            name: "special",
            filterQuery: testFilter,
            column,
        };
        // const columnData =
        this._thenAdvancedFilters[1] = new AdvancedFilter({
            filterData: {},
            columnsData: [column],
            type: type,
        });

        this.thenContainer = document.createElement("div");
        const thenLabel = document.createElement("label");
        this.thenContainer.classList.add("then-container");
        thenLabel.innerText = "THEN";
        this.thenContainer.appendChild(thenLabel);

        this._thenAdvancedFilters.forEach((af) => {
            this.thenContainer.appendChild(af.renderer._rootElem);
            af.renderer._rootElem.classList.add("hidden-filter");
        });

        const form = this._formsComponent.getValue();

        if (
            _.get(form, "sourceQuery.source", "LATEST") === "LATEST" &&
            this._model._currentRow.thenExpression
        ) {
            this._thenFilterExpression = this.findInFilterOpposite(
                this._model._currentRow.thenExpression
            );
            this._thenAdvancedFilters[0].setQuery(this._thenFilterExpression);
            this._thenAdvancedFilters[1].setQuery(
                JSON.parse(
                    JSON.stringify(this._thenFilterExpression).replaceAll(
                        "latestConfig",
                        "defaultConfig"
                    )
                )
            );
        } else if (
            _.get(form, "sourceQuery.source", "LATEST") === "DEFAULT" &&
            this._model._currentRow.thenExpression
        ) {
            this._thenFilterExpression = this.findInFilterOpposite(
                this._model._currentRow.thenExpression
            );
            this._thenAdvancedFilters[1].setQuery(this._thenFilterExpression);
            this._thenAdvancedFilters[0].setQuery(
                JSON.parse(
                    JSON.stringify(this._thenFilterExpression).replaceAll(
                        "defaultConfig",
                        "latestConfig"
                    )
                )
            );
        }

        return this.thenContainer;
    }

    _renderElseAdvancedFilter() {
        this._elseAdvancedFilters = [];
        let column = {},
            testFilter = {};
        // if(_.get(form, 'sourceQuery.source', 'LATEST') === 'LATEST') {
        column = {
            field: "latestConfig",
            id: "latestConfig",
            input: "textarea",
            label: "Latest Snapshot",
            type: "string",
            values: [],
        };
        testFilter = {
            condition: "AND",
            rules: [
                {
                    id: "latestConfig",
                    operator: "should_contain",
                    value: "a",
                },
            ],
        };

        let type = {
            name: "special",
            filterQuery: testFilter,
            column,
        };
        // const columnData =
        this._elseAdvancedFilters[0] = new AdvancedFilter({
            filterData: {},
            columnsData: [column],
            type: type,
        });

        column = {
            field: "defaultConfig",
            id: "defaultConfig",
            input: "textarea",
            label: "Default Snapshot",
            type: "string",
            values: [],
        };
        testFilter = {
            condition: "AND",
            rules: [
                {
                    id: "defaultConfig",
                    operator: "should_contain",
                    value: "a",
                },
            ],
        };

        type = {
            name: "special",
            filterQuery: testFilter,
            column,
        };
        // const columnData =
        this._elseAdvancedFilters[1] = new AdvancedFilter({
            filterData: {},
            columnsData: [column],
            type: type,
        });

        this.elseContainer = document.createElement("div");
        const elseLabel = document.createElement("label");
        this.elseContainer.classList.add("else-container");
        elseLabel.innerText = "ELSE";
        this.elseContainer.appendChild(elseLabel);
        this._elseAdvancedFilters.forEach((af) => {
            this.elseContainer.appendChild(af.renderer._rootElem);
            af.renderer._rootElem.classList.add("hidden-filter");
        });

        const form = this._formsComponent.getValue();
        if (
            _.get(form, "sourceQuery.source", "LATEST") === "LATEST" &&
            this._model._currentRow.elseExpression
        ) {
            this._elseFilterExpression = this.findInFilterOpposite(
                this._model._currentRow.elseExpression
            );
            this._elseAdvancedFilters[0].setQuery(this._elseFilterExpression);
            this._elseAdvancedFilters[1].setQuery(
                JSON.parse(
                    JSON.stringify(this._elseFilterExpression).replaceAll(
                        "latestConfig",
                        "defaultConfig"
                    )
                )
            );
        } else if (
            _.get(form, "sourceQuery.source", "LATEST") === "DEFAULT" &&
            this._model._currentRow.elseExpression
        ) {
            this._elseFilterExpression = this.findInFilterOpposite(
                this._model._currentRow.elseExpression
            );
            this._elseAdvancedFilters[1].setQuery(this._elseFilterExpression);
            this._elseAdvancedFilters[0].setQuery(
                JSON.parse(
                    JSON.stringify(this._elseFilterExpression).replaceAll(
                        "defaultConfig",
                        "latestConfig"
                    )
                )
            );
        }

        return this.elseContainer;
    }

    _renderConceptItemCommands() {
        const commandResultDropdown =
                this._commandSetResultDropdown.getValue() === "basicMode"
                    ? document.querySelector(".command-set-form-container .section_elem")
                    : document.querySelector(".command-set-form-container .command_set_result"),
            parentGameEditor = _.get(this._model, "_parentView._gameEditor"),
            commandSetResult =
                this._commandSetResultDropdown.getValue() === "basicMode"
                    ? this._sectionDropdown.getValue()
                    : this._commandSetResultDropdown.getValue();

        let result = [];
        this._toolTipContent = "";
        if (commandSetResult.indexOf("concept-item.") > -1) {
            const currentConceptId = commandSetResult.replace(/concept-item./, "");
            result = parentGameEditor._conceptItems[currentConceptId].showCommands;
        } else if (commandSetResult.indexOf("show-command.") > -1) {
            result = commandSetResult.replace(/show-command./, "");
        } else if (commandSetResult.indexOf("rsi-id.") > -1) {
            const currentRSIId = commandSetResult.replace(/rsi-id./, "");
            result = parentGameEditor._rsiIdsMap[currentRSIId].readOperation;
        } else if (commandSetResult.indexOf("get-url.") > -1) {
            result = commandSetResult.replace(/get-url./, "");
        } else if (commandSetResult === "all") {
            result = this._getAllConfigsForTooltip();
        }

        this._toolTipContent = _.clone(result);

        this._removeOldCommandValues();

        if (_.isEmpty(_.trim(result))) {
            return;
        }

        const commandContainer = document.createElement("div");
        commandContainer.classList.add("additional-command-container");
        const innerContainer = document.createElement("div");
        innerContainer.classList.add("additional-inner");
        this._viewMoreIcon = document.createElement("actionable-icon-gl");
        this._viewMoreIcon.dataset.icon = "configdrift.view_more";
        this._viewMoreIcon.setAttribute("title", "Command Result");
        commandContainer.appendChild(innerContainer);
        commandContainer.appendChild(this._viewMoreIcon);

        commandResultDropdown.appendChild(commandContainer);

        if (_.isArray(result)) {
            let resString = "";
            result.forEach((i) => {
                resString += i + " ";
            });
            innerContainer.innerHTML = resString;
        } else {
            innerContainer.innerHTML = result;
        }
        this._createPopUp();

        return Promise.resolve();
    }

    _createPopUp() {
        if (this._rootElem === null) {
            return;
        }
        const viewMoreContentElement = document.createElement("view-more-pop-up");
        viewMoreContentElement.classList.add("command-set-results-popup");
        viewMoreContentElement.innerHTML = this._toolTipContent;
        this._popUpWidget = new PopUpWidget({
            content: viewMoreContentElement.outerHTML,
            anchor: this._viewMoreIcon,
            anchorPoint: "right",
            displayDirection: "bottomRight",
            openOnHover: true,
            parent: viewMoreContentElement.parentNode,
        });

        this._registerPopUpListener();
    }

    _getAllConfigsForTooltip() {
        let strRes = "";
        const options = this._commandSetResultDropdown.getOptions();
        const _gameEditor = this._model._parentView._gameEditor;
        options.forEach((item) => {
            if (item.value.indexOf("concept-item.") > -1) {
                const currentConceptItemId = item.value.replace(/concept-item./, "");
                const conceptItem = _gameEditor._conceptItems[currentConceptItemId];
                const commands = conceptItem.showCommands;
                if (commands && _.isArray(commands)) {
                    commands.forEach((i) => {
                        strRes += i + "<br />";
                    });
                }
            }

            if (item.value.indexOf("rsi-id.") > -1) {
                const rsiId = item.value.replace(/rsi-id./, "");
                const rsiObject = _gameEditor._rsiIdsMap[rsiId];
                const commands = rsiObject.readOperation;
                strRes += commands + "<br />";
            }

            if (item.value.indexOf("get-url.") > -1) {
                const getURL = item.value.replace(/get-url./, "");
                strRes += getURL + "<br />";
            }

            if (item.value.indexOf("show-command.") > -1) {
                const showCommand = item.value.replace(/show-command./, "");
                strRes += showCommand + "<br />";
            }
        });

        return strRes;
    }

    _removeOldCommandValues() {
        const oldCommandValuesCommandSetResult = document.querySelector(
            ".command-set-form-container .command_set_result .additional-command-container"
        );
        const oldCommandValuesSection = document.querySelector(
            ".command-set-form-container .section_elem .additional-command-container"
        );

        if (oldCommandValuesCommandSetResult) {
            oldCommandValuesCommandSetResult.remove();
        }
        if (oldCommandValuesSection) {
            oldCommandValuesSection.remove();
        }
    }

    _updateThenAdvancedFilterExpression() {
        this._thenFilterExpression = _.cloneDeep(
            this._thenAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules")
        );
        if (this._thenFilterExpression) {
            this._thenFilterExpression = this.findInFilter(this._thenFilterExpression);
        }
    }

    _updateElseAdvancedFilterExpression() {
        this._elseFilterExpression = _.cloneDeep(
            this._elseAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules")
        );
        if (this._elseFilterExpression) {
            this._elseFilterExpression = this.findInFilter(this._elseFilterExpression);
        }
    }

    _getShowCommands(commandSet, resultObject) {
        const commands = commandSet.getCommands();
        if (commands && _.isArray(commands)) {
            commands.forEach((command) => {
                const exists = resultObject.find((i) => i.value === `show-command.${command}`);

                if (_.isEmpty(exists)) {
                    resultObject.push({value: `show-command.${command}`, label: command});
                }
            });
        }
    }
    _getConceptItems(commandSet, resultObject) {
        const conceptItems = commandSet.getConceptItemIds();
        if (conceptItems) {
            conceptItems.forEach((conceptItem) => {
                const exists = resultObject.find((i) => i.value === `concept-item.${conceptItem}`);

                if (_.isEmpty(exists)) {
                    resultObject.push({
                        value: `concept-item.${conceptItem}`,
                        label: _.get(
                            this.model._parentView.gameEditor.conceptItems,
                            `${conceptItem}.label`
                        ),
                    });
                }
            });
        }
    }
    _getRSIIds(commandSet, resultObject) {
        const rsiIds = commandSet.getRsiIds();
        if (rsiIds) {
            rsiIds.forEach((rsiId) => {
                const rsi = _.get(this.model._parentView.gameEditor, "_rsiIds", []).find(
                    (i) => i.value === rsiId
                );
                // check for unique
                const exists = resultObject.find((i) => i.value === `rsi-id.${rsiId}`);

                if (_.isEmpty(exists)) {
                    resultObject.push({
                        value: `rsi-id.${rsiId}`,
                        label: _.get(rsi, "label"),
                    });
                }
            });
        }
    }
    _getGetURLs(commandSet, resultObject) {
        const getURLs = commandSet.getGetUrls();
        if (getURLs) {
            getURLs.forEach((getURL) => {
                const exists = resultObject.find((i) => i.value === `get-url.${getURL}`);

                if (_.trim(getURL) !== "" && _.isEmpty(exists)) {
                    resultObject.push({value: `get-url.${getURL}`, label: getURL});
                }
            });
        }
    }

    _getAllRSIIds(resultObject, os = undefined) {
        const _gameEditor = this._model._parentView._gameEditor;
        let rsiIds = [];
        if (os === " All") {
            rsiIds = _gameEditor._rsiIds.map((i) => ({label: i.label, value: `rsi-id.${i.value}`}));
        } else {
            rsiIds = _gameEditor._rsiIds
                .filter((i) => {
                    return i.os && i.os.indexOf(os) > -1;
                })
                .map((i) => ({label: i.label, value: `rsi-id.${i.value}`}));
        }

        resultObject.push(...rsiIds);
    }
    _getAllConceptItems(resultObject, os = undefined) {
        const _gameEditor = this._model._parentView._gameEditor;
        let conceptItems = [];
        if (os === " All") {
            conceptItems = Object.values(_gameEditor._conceptItems).map((i) => ({
                label: i.label,
                value: `concept-item.${i.value}`,
            }));
        } else {
            conceptItems = Object.values(_gameEditor._conceptItems)
                .filter((i) => {
                    return i.os && i.os.indexOf(os) > -1;
                })
                .map((i) => ({
                    label: i.label,
                    value: `concept-item.${i.value}`,
                }));
        }
        resultObject.push(...conceptItems);
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = EditAuditPolicyViewRenderer;
